import { VitrineBadge } from '../components/simulation/VitrineBadge';
import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { usePermissions } from '../contexts/PermissionsContext';
import { AppLayout } from '../components/AppLayout';
import { CommercialWeatherWidget } from '../components/CommercialWeatherWidget';
import { WeatherWidget } from '../components/WeatherWidget';
import { DashboardModules } from '../components/DashboardModules';
import { SimulationStatusBar } from '../components/SimulationStatusBar';

import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from '../components/ui/sheet';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Checkbox } from '../components/ui/checkbox';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { toast } from 'sonner';
import { BusinessStatusBadge, UserKPIsTable, SimulationFilters, KPIDashboard, GoldenInfoWidget, ContractRequestsWidget } from '../components/dashboard';
import { AnnouncementEditorModal } from '../components/announcements/AnnouncementEditorModal';
import ReassignSimulationDialog from '../components/ReassignSimulationDialog';
import { 
  PlusCircle, 
  TrendingUp, 
  TrendingDown,
  Calculator, 
  Clock, 
  ChevronRight,
  BarChart3,
  Loader2,
  CheckCircle2,
  XCircle,
  PieChart,
  Search,
  FileText,
  AlertTriangle,
  Eye,
  EyeOff,
  ThumbsUp,
  ThumbsDown,
  HelpCircle,
  User,
  Users,
  Building2,
  RefreshCw,
  Trash2,
  Download,
  Bell,
  ArrowUpDown,
  Filter,
  SlidersHorizontal,
  CalendarDays,
  Trophy,
  MessageSquare,
  Send,
  UserCheck,
  Mail,
  Ban,
  FileSignature,
  Megaphone,
  FileEdit,
  UserCog
} from 'lucide-react';
import { formatDistanceToNow, format, parseISO, startOfMonth, subMonths } from 'date-fns';
import { fr, enUS } from 'date-fns/locale';
import { 
  BarChart, 
  Bar, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  Legend, 
  ResponsiveContainer,
  PieChart as RechartsPieChart,
  Pie,
  Cell
} from 'recharts';

const formatCurrency = (value) => {
  return new Intl.NumberFormat('fr-FR', {
    style: 'currency',
    currency: 'EUR',
    maximumFractionDigits: 0,
  }).format(value);
};

// Pictogramme du statut de l'annonce ALL d'un dossier (cliquable → ouvre l'annonce concernée).
const renderAnnPicto = (status, language, simId, onOpen) => {
  if (!status) return null;
  const map = {
    valide: { Icon: CheckCircle2, cls: 'text-emerald-600 bg-emerald-50 border-emerald-200 dark:bg-emerald-900/30 dark:text-emerald-300 dark:border-emerald-800', fr: 'Annonce ALL déjà faite (validée) — cliquer pour ouvrir', en: 'ALL announcement done (approved) — click to open' },
    en_attente: { Icon: Clock, cls: 'text-amber-600 bg-amber-50 border-amber-200 dark:bg-amber-900/30 dark:text-amber-300 dark:border-amber-800', fr: 'Annonce ALL soumise — en attente de validation — cliquer pour ouvrir', en: 'ALL announcement submitted — pending validation — click to open' },
    brouillon: { Icon: FileEdit, cls: 'text-slate-500 bg-slate-100 border-slate-200 dark:bg-slate-700 dark:text-slate-300 dark:border-slate-600', fr: 'Annonce ALL en brouillon — cliquer pour ouvrir', en: 'ALL announcement draft — click to open' },
    refuse: { Icon: XCircle, cls: 'text-red-600 bg-red-50 border-red-200 dark:bg-red-900/30 dark:text-red-300 dark:border-red-800', fr: 'Annonce ALL refusée — cliquer pour ouvrir', en: 'ALL announcement rejected — click to open' },
  };
  const cfg = map[status];
  if (!cfg) return null;
  const Icon = cfg.Icon;
  return (
    <button
      type="button"
      onClick={(e) => { e.stopPropagation(); onOpen && onOpen(); }}
      className={`inline-flex items-center justify-center w-6 h-6 rounded-full border shadow-sm transition-transform hover:scale-110 focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-emerald-400 cursor-pointer ${cfg.cls}`}
      title={language === 'fr' ? cfg.fr : cfg.en}
      data-testid={`ann-status-picto-${simId}`}
    >
      <Icon className="w-3.5 h-3.5" />
    </button>
  );
};


// Safe date formatting function to prevent crashes on invalid dates
const safeFormatDate = (dateString, formatStr, locale) => {
  if (!dateString) return 'N/A';
  try {
    const date = parseISO(dateString);
    if (isNaN(date.getTime())) return 'N/A';
    return format(date, formatStr, { locale });
  } catch (e) {
    console.warn('Invalid date:', dateString, e);
    return 'N/A';
  }
};

// Safe date distance formatting
const safeFormatDistanceToNow = (dateString, options) => {
  if (!dateString) return 'N/A';
  try {
    const date = parseISO(dateString);
    if (isNaN(date.getTime())) return 'N/A';
    return formatDistanceToNow(date, options);
  } catch (e) {
    console.warn('Invalid date for distance:', dateString, e);
    return 'N/A';
  }
};

// Helper function to get display name for a simulation (Enseigne + Ville)
const getSimulationDisplayName = (sim) => {
  const retailer = sim.input_data?.general_info?.retailer_name || '';
  const city = sim.input_data?.general_info?.city || '';
  
  if (retailer && city) {
    return `${retailer} ${city}`;
  } else if (retailer) {
    return retailer;
  } else if (city) {
    return city;
  }
  // Fallback to project_name if no retailer/city
  return sim.project_name || 'Sans nom';
};

const VerdictBadge = ({ verdict, adminStatus, businessStatus, counterProposalPending }) => {
  // Si le dossier est "Signé", on n'affiche plus le badge contre-proposition
  // On montre GO ✓ car signé = validé commercialement
  if (businessStatus === 'signed') {
    return <Badge className="bg-emerald-100 text-emerald-700">GO ✓</Badge>;
  }
  
  // Si le dossier est "NO GO", afficher NO GO
  if (businessStatus === 'nogo') {
    return <Badge className="bg-red-100 text-red-700">NO GO ✗</Badge>;
  }
  
  // Si le dossier est "Perdu", afficher NO GO  
  if (businessStatus === 'lost') {
    return <Badge className="bg-red-100 text-red-700">NO GO ✗</Badge>;
  }
  
  // Si une contre-proposition est en attente de réponse (et pas signé), afficher ce statut
  if (counterProposalPending) {
    return <Badge className="bg-orange-100 text-orange-700 border border-orange-300">⏳ Contre-proposition en attente</Badge>;
  }
  
  // Si le dossier est marqué comme "Gagné", ne pas afficher "À L'ÉTUDE"
  if (businessStatus === 'won') {
    return <Badge className="bg-emerald-100 text-emerald-700">GO ✓</Badge>;
  }
  
  // Décision Direction (après validation commerciale)
  if (adminStatus === 'approved') {
    return <Badge className="bg-emerald-100 text-emerald-700">GO ✓</Badge>;
  }
  if (adminStatus === 'rejected') {
    return <Badge className="bg-red-100 text-red-700">NO GO ✗</Badge>;
  }
  
  // Par défaut (aucune décision Direction prise) : À L'ÉTUDE
  // Que ce soit pending_review ou null, on affiche À L'ÉTUDE
  return <Badge className="bg-amber-100 text-amber-700">À L'ÉTUDE</Badge>;
};

const COLORS = {
  viable: '#10b981',
  risky: '#f59e0b',
  'non-viable': '#ef4444',
};

export default function DashboardPage({ view = 'main' }) {
  const { api, user, token } = useAuth();
  const { t, language } = useLanguage();
  const { hasPermission } = usePermissions();
  const navigate = useNavigate();
  const [simulations, setSimulations] = useState([]);
  const [reassignTarget, setReassignTarget] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [mobileFilterOpen, setMobileFilterOpen] = useState(false);
  const [filterHasComments, setFilterHasComments] = useState(false); // Show only sims with CAPEX comments
  const [actionLoading, setActionLoading] = useState(false);
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterCarrier, setFilterCarrier] = useState('all');
  const [filterCommercial, setFilterCommercial] = useState('all');
  const [filterBusinessStatus, setFilterBusinessStatus] = useState('all'); // 'all', 'won', 'lost', 'in_progress', 'nogo'
  const [searchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState(() => {
    const t = searchParams.get('tab');
    return ['all', 'aTraiter', 'enCours', 'signees', 'perdues'].includes(t) ? t : 'all';
  }); // onglet de statut cliquable (barre de compteurs)
  const [retailerFilter, setRetailerFilter] = useState(null); // filtre par enseigne (clic sur le graphe payback)
  const [sortBy, setSortBy] = useState('date_desc');
  const [reminderLoading, setReminderLoading] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);
  const [recalcLoading, setRecalcLoading] = useState(false);
  
  // Dialog state for status update
  const [statusDialogOpen, setStatusDialogOpen] = useState(false);
  const [selectedSimulation, setSelectedSimulation] = useState(null);
  const [statusAction, setStatusAction] = useState('');
  const [statusComment, setStatusComment] = useState('');
  
  // Dialog state for delete confirmation
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [simulationToDelete, setSimulationToDelete] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  
  // HG send state
  const [sendingToHg, setSendingToHg] = useState(null);
  
  // Announcement ALL state
  const [showAnnouncementModal, setShowAnnouncementModal] = useState(false);
  const [announcementSimId, setAnnouncementSimId] = useState(null);
  const [annStatusMap, setAnnStatusMap] = useState({});
  const [annSentCountMap, setAnnSentCountMap] = useState({});
  const [annFilter, setAnnFilter] = useState('all'); // all | done | todo (filtre dossiers signés)
  
  const [stats, setStats] = useState({
    total: 0,
    viable: 0,
    risky: 0,
    nonViable: 0,
    avgCapex: 0,
    avgPayback: 0,
    pending: 0,
    approved: 0,
    rejected: 0,
    pendingReview: 0,
    totalCaGo: 0,
    totalCapexEngaged: 0,
    avgPaybackGo: 0,
  });
  
  // KPI time period filter
  const [kpiPeriod, setKpiPeriod] = useState('month'); // 'month', '3months', 'year'
  
  // Force refresh key for KPIs
  const [kpiRefreshKey, setKpiRefreshKey] = useState(0);

  // Check if user is admin or super_admin
  const isAdmin = user?.role === 'admin' || user?.role === 'super_admin';
  const isSuperAdmin = user?.role === 'super_admin';
  
  // State for "Standard User View" mode (allows admin to see app as regular user)
  const [isStandardUserView, setIsStandardUserView] = useState(false);
  
  // Effective permissions based on view mode
  const effectiveIsAdmin = isAdmin && !isStandardUserView;
  const effectiveIsSuperAdmin = isSuperAdmin && !isStandardUserView;
  
  // Permission-based access control (disabled in standard user view)
  const canMakeDecision = hasPermission('make_decision') && !isStandardUserView;
  const canDeleteSimulation = hasPermission('delete_simulation') && !isStandardUserView;
  const canSendToHg = hasPermission('send_to_hg') && !isStandardUserView;
  const canChangeBusinessStatus = isSuperAdmin && !isStandardUserView;
  const canViewAllSimulations = hasPermission('view_all_simulations') && !isStandardUserView;
  const canViewAnalytics = hasPermission('view_analytics') && !isStandardUserView;
  const canCreateSimulation = hasPermission('create_simulation');

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await api().get('/simulations');
        setSimulations(response.data);
        
        // Calculate stats
        if (response.data.length > 0) {
          const viable = response.data.filter(s => s.status === 'viable').length;
          const risky = response.data.filter(s => s.status === 'risky').length;
          const nonViable = response.data.filter(s => s.status === 'non-viable').length;
          const totalCapex = response.data.reduce((sum, s) => sum + s.total_capex, 0);
          const totalPayback = response.data.reduce((sum, s) => sum + s.payback_months, 0);
          const approved = response.data.filter(s => s.admin_status === 'approved');
          const rejected = response.data.filter(s => s.admin_status === 'rejected').length;
          const pendingReview = response.data.filter(s => s.admin_status === 'pending_review').length;
          const pending = response.data.filter(s => !s.admin_status).length;
          
          const totalCaGo = approved.reduce((sum, s) => 
            sum + (s.admin_counter_proposal_ca || s.ca_previsionnel_ht_proposed || s.ca_potential_ht || 0), 0);
          const totalCapexEngaged = approved.reduce((sum, s) => sum + (s.total_capex || 0), 0);
          const avgPaybackGo = approved.length > 0 
            ? approved.reduce((sum, s) => sum + (s.payback_months || 0), 0) / approved.length 
            : 0;
          
          setStats({
            total: response.data.length,
            viable,
            risky,
            nonViable,
            avgCapex: totalCapex / response.data.length,
            avgPayback: totalPayback / response.data.length,
            pending,
            approved: approved.length,
            rejected,
            pendingReview,
            totalCaGo,
            totalCapexEngaged,
            avgPaybackGo,
          });
        }
      } catch (error) {
        console.error('Failed to fetch simulations:', error);
        toast.error(t('error'));
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [api, t]);

  // Statut des annonces ALL par simulation (pour le pictogramme « annonce déjà faite »).
  const refreshAnnStatuses = React.useCallback(async () => {
    try {
      const resp = await api().get('/announcements');
      const map = {};
      const sentCount = {};
      (resp.data || []).forEach(a => {
        // Garde le statut de l'annonce la plus récente (liste triée par created_at desc côté API).
        if (a.simulation_id && !(a.simulation_id in map)) map[a.simulation_id] = a.status;
        // Compte les annonces effectivement envoyées (validées) par dossier.
        if (a.simulation_id && a.status === 'valide') {
          sentCount[a.simulation_id] = (sentCount[a.simulation_id] || 0) + 1;
        }
      });
      setAnnStatusMap(map);
      setAnnSentCountMap(sentCount);
    } catch (e) {
      // silencieux : le pictogramme est un bonus non bloquant
    }
  }, [api]);

  useEffect(() => {
    refreshAnnStatuses();
  }, [refreshAnnStatuses]);

  const dateLocale = language === 'fr' ? fr : enUS;

  // Handle business status change (Gagné/Perdu/En cours)
  const [businessStatusLoading, setBusinessStatusLoading] = useState(null);
  const [bsPrompt, setBsPrompt] = useState(null); // {simId, status}
  const [bsMotif, setBsMotif] = useState('');
  const [bsDate, setBsDate] = useState('');
  const [motifsList, setMotifsList] = useState([]);
  useEffect(() => {
    api().get('/referentiels/motifs_perte').then(r => setMotifsList((r.data?.values || []).map(v => v.value))).catch(() => {});
  }, [api]);

  const renderBsPrompt = () => {
    if (!bsPrompt) return null;
    const isLost = bsPrompt.status === 'lost' || bsPrompt.status === 'nogo';
    const confirm = () => {
      if (isLost && !bsMotif) { toast.error('Sélectionnez un motif de perte'); return; }
      if (!isLost && !bsDate) { toast.error('Sélectionnez une date de signature'); return; }
      const p = bsPrompt;
      const extra = isLost ? { motif_perte: bsMotif } : { date_signature: bsDate };
      setBsPrompt(null);
      handleBusinessStatusChange(p.simId, p.status, extra);
    };
    return (
      <div className="fixed inset-0 z-[200] flex items-center justify-center bg-black/50 p-4" data-testid="bs-prompt-modal">
        <div className="w-full max-w-sm rounded-2xl bg-white dark:bg-slate-900 p-5 shadow-2xl">
          <h3 className="font-semibold text-slate-900 dark:text-white mb-1">{isLost ? 'Motif de perte' : 'Date de signature'}</h3>
          <p className="text-xs text-slate-500 mb-4">Champ obligatoire pour finaliser ce statut.</p>
          {isLost ? (
            <select value={bsMotif} onChange={(e) => setBsMotif(e.target.value)}
              className="w-full rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange-400" data-testid="bs-motif-select">
              <option value="">— Choisir un motif —</option>
              {motifsList.map((m) => <option key={m} value={m}>{m}</option>)}
            </select>
          ) : (
            <input type="date" value={bsDate} onChange={(e) => setBsDate(e.target.value)}
              className="w-full rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange-400" data-testid="bs-date-input" />
          )}
          <div className="flex justify-end gap-2 mt-5">
            <button onClick={() => setBsPrompt(null)} className="px-3 py-2 rounded-lg text-sm text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800" data-testid="bs-cancel">Annuler</button>
            <button onClick={confirm} className="px-4 py-2 rounded-lg text-sm font-semibold bg-orange-500 text-white hover:bg-orange-600" data-testid="bs-confirm">Confirmer</button>
          </div>
        </div>
      </div>
    );
  };
  
  const handleBusinessStatusChange = async (simId, newBusinessStatus, extra = {}) => {
    if ((newBusinessStatus === 'lost' || newBusinessStatus === 'nogo') && !extra.motif_perte) {
      setBsPrompt({ simId, status: newBusinessStatus }); setBsMotif(''); setBsDate(''); return;
    }
    if ((newBusinessStatus === 'signed' || newBusinessStatus === 'won') && !extra.date_signature) {
      setBsPrompt({ simId, status: newBusinessStatus }); setBsMotif(''); setBsDate(''); return;
    }
    setBusinessStatusLoading(simId);
    try {
      await api().patch(`/simulations/${simId}/business-status`, {
        business_status: newBusinessStatus,
        ...extra,
      });
      
      // Update local state - create entirely new array to trigger re-render
      setSimulations(prev => {
        const newSimulations = prev.map(sim => {
          if (sim.id === simId) {
            return { ...sim, business_status: newBusinessStatus };
          }
          return sim;
        });
        return [...newSimulations]; // Force new array reference
      });
      
      // Force KPI recalculation
      setKpiRefreshKey(prev => prev + 1);
      
      const statusLabels = {
        signed: language === 'fr' ? 'Dossier Signé' : 'Signed',
        lost: language === 'fr' ? 'Dossier Perdu' : 'Lost',
        nogo: language === 'fr' ? 'Dossier NO GO' : 'NO GO',
        in_progress: language === 'fr' ? 'Dossier en Cours' : 'In Progress'
      };
      
      toast.success(language === 'fr' 
        ? `Statut changé en "${statusLabels[newBusinessStatus]}"` 
        : `Status changed to "${statusLabels[newBusinessStatus]}"`
      );
    } catch (error) {
      console.error('Failed to update business status:', error);
      const detail = error?.response?.data?.detail;
      toast.error(detail || (language === 'fr' ? 'Erreur lors du changement de statut' : 'Failed to update status'));
    } finally {
      setBusinessStatusLoading(null);
    }
  };

  // Calculate User KPIs based on selected period
  const calculatedUserKpis = useMemo(() => {
    if (!simulations.length) return [];
    
    const now = new Date();
    let startDate;
    
    switch (kpiPeriod) {
      case 'month':
        startDate = startOfMonth(now);
        break;
      case '3months':
        startDate = subMonths(now, 3);
        break;
      case 'year':
        startDate = subMonths(now, 12);
        break;
      default:
        startDate = startOfMonth(now);
    }
    
    // Filter simulations by period
    const periodSimulations = simulations.filter(sim => {
      const simDate = new Date(sim.created_at);
      return simDate >= startDate;
    });
    
    // Group by user
    const userMap = {};
    
    periodSimulations.forEach(sim => {
      const userName = sim.creator_name || sim.creator_email || 'Inconnu';
      const userEmail = sim.creator_email || '';
      
      if (!userMap[userName]) {
        userMap[userName] = {
          name: userName,
          email: userEmail,
          created: 0,
          won: 0,
          lost: 0,
          inProgress: 0,
          totalCa: 0,
        };
      }
      
      userMap[userName].created++;
      
      // Determine business status - EXPLICIT business_status has priority
      // Count 'signed' and 'won' as signed (backward compatibility)
      if (sim.business_status === 'signed' || sim.business_status === 'won') {
        userMap[userName].won++;
        userMap[userName].totalCa += sim.ca_previsionnel_ht_proposed || sim.ca_potential_ht || 0;
      } else if (sim.business_status === 'lost') {
        userMap[userName].lost++;
      } else if (sim.business_status === 'in_progress') {
        userMap[userName].inProgress++;
      } else {
        // If no explicit business_status, count as in_progress
        userMap[userName].inProgress++;
      }
    });
    
    // Convert to array and sort by created count
    return Object.values(userMap).sort((a, b) => b.created - a.created);
  }, [simulations, kpiPeriod, kpiRefreshKey]);

  // Get unique commercial names
  const commercialNames = useMemo(() => {
    const names = [...new Set(simulations.map(s => s.creator_name).filter(Boolean))];
    return names.sort();
  }, [simulations]);

  // Filter simulations based on search query and filters
  const filteredSimulations = useMemo(() => {
    let result = simulations;
    
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      result = result.filter(sim => 
        getSimulationDisplayName(sim).toLowerCase().includes(query) ||
        sim.project_name?.toLowerCase().includes(query) ||
        sim.input_data?.general_info?.address?.toLowerCase().includes(query) ||
        sim.input_data?.general_info?.retailer_name?.toLowerCase().includes(query) ||
        sim.input_data?.general_info?.city?.toLowerCase().includes(query) ||
        sim.creator_name?.toLowerCase().includes(query)
      );
    }
    
    if (filterStatus !== 'all') {
      if (filterStatus === 'pending') result = result.filter(s => !s.admin_status);
      else result = result.filter(s => s.admin_status === filterStatus);
    }
    
    if (filterCarrier !== 'all') {
      result = result.filter(s => s.capex_carrier === filterCarrier);
    }
    
    if (filterCommercial !== 'all') {
      result = result.filter(s => s.creator_name === filterCommercial);
    }
    
    // Filter by business status (Gagné / Perdu / NO GO / En cours)
    // EXPLICIT business_status has priority over inferred status
    if (filterBusinessStatus !== 'all') {
      result = result.filter(s => {
        // If explicit business_status exists, use it
        if (s.business_status) {
          if (filterBusinessStatus === 'won') return s.business_status === 'won' || s.business_status === 'signed';
          if (filterBusinessStatus === 'lost') return s.business_status === 'lost';
          if (filterBusinessStatus === 'nogo') return s.business_status === 'nogo';
          if (filterBusinessStatus === 'in_progress') return s.business_status === 'in_progress';
        }
        
        // Otherwise, infer from admin_status
        const isWon = s.admin_status === 'approved' && s.verdict === 'GO';
        const isLost = s.admin_status === 'rejected';
        
        if (filterBusinessStatus === 'won') return isWon;
        if (filterBusinessStatus === 'lost') return isLost;
        if (filterBusinessStatus === 'nogo') return isLost; // NO GO = rejected
        if (filterBusinessStatus === 'in_progress') return !isWon && !isLost;
        return true;
      });
    }

    // Filter "with comments only"
    if (filterHasComments) {
      result = result.filter(s => (s.capex_comments_count || 0) > 0);
    }
    
    // Sort
    result = [...result].sort((a, b) => {
      const getValidDate = (dateStr) => {
        if (!dateStr) return new Date(0);
        const d = new Date(dateStr);
        return isNaN(d.getTime()) ? new Date(0) : d;
      };
      
      switch (sortBy) {
        case 'date_asc': return getValidDate(a.created_at) - getValidDate(b.created_at);
        case 'date_desc': return getValidDate(b.created_at) - getValidDate(a.created_at);
        case 'ca_desc': {
          const caA = a.admin_counter_proposal_ca || a.ca_previsionnel_ht_proposed || a.ca_potential_ht || 0;
          const caB = b.admin_counter_proposal_ca || b.ca_previsionnel_ht_proposed || b.ca_potential_ht || 0;
          return caB - caA;
        }
        case 'ca_asc': {
          const caA2 = a.admin_counter_proposal_ca || a.ca_previsionnel_ht_proposed || a.ca_potential_ht || 0;
          const caB2 = b.admin_counter_proposal_ca || b.ca_previsionnel_ht_proposed || b.ca_potential_ht || 0;
          return caA2 - caB2;
        }
        case 'commission_desc': {
          const cA = a.admin_counter_proposal_commission || a.commission_rate_proposed || a.commission_rate_used || 0;
          const cB = b.admin_counter_proposal_commission || b.commission_rate_proposed || b.commission_rate_used || 0;
          return cB - cA;
        }
        default: return getValidDate(b.created_at) - getValidDate(a.created_at);
      }
    });
    
    return result;
  }, [simulations, searchQuery, filterStatus, filterCarrier, filterCommercial, filterBusinessStatus, filterHasComments, sortBy]);

  // Statut "à traiter" = en attente de décision admin
  const isATraiter = (s) => !s.admin_status || s.admin_status === 'pending_review' || s.admin_status === 'under_review';

  // Liste affichée = filtres courants + onglet de statut actif (barre de compteurs cliquable)
  const tabFilteredSimulations = useMemo(() => {
    if (activeTab === 'all') return filteredSimulations;
    return filteredSimulations.filter((s) => {
      if (activeTab === 'aTraiter') return isATraiter(s);
      const biz = s.business_status || 'in_progress';
      if (activeTab === 'enCours') return biz === 'in_progress';
      if (activeTab === 'signees') return biz === 'signed' || biz === 'won';
      if (activeTab === 'perdues') return biz === 'lost' || biz === 'nogo';
      return true;
    });
  }, [filteredSimulations, activeTab]);

  // Liste réellement affichée = onglet + filtre enseigne (le graphe payback reste basé sur tabFilteredSimulations)
  const enseigneKey = (s) => ((s?.input_data?.general_info?.retailer_name || '').trim()) || '__AUTRE__';
  const listSimulations = useMemo(() => {
    if (retailerFilter == null) return tabFilteredSimulations;
    return tabFilteredSimulations.filter((s) => enseigneKey(s) === retailerFilter);
  }, [tabFilteredSimulations, retailerFilter]);

  // Group simulations by business status for sectioned display
  const groupedSimulations = useMemo(() => {
    const getBusinessStatus = (sim) => {
      if (sim.business_status) return sim.business_status;
      // Default to in_progress if no explicit status
      return 'in_progress';
    };

    const groups = {
      a_traiter: [], // Etude à traiter (Direction) — en attente de décision Direction
      in_progress: [],
      signed: [], // Dossier Signé
      nogo: [],   // Dossier NO GO
      lost: []    // Dossier Perdu
    };

    listSimulations.forEach(sim => {
      const status = getBusinessStatus(sim);
      if (status === 'signed' || status === 'won') {
        // Map 'won' to 'signed' for backward compatibility
        groups.signed.push(sim);
      } else if (status === 'nogo') {
        groups.nogo.push(sim);
      } else if (status === 'lost') {
        groups.lost.push(sim);
      } else if (isATraiter(sim)) {
        // Ni signé ni perdu, en attente de décision Direction (pas encore de GO admin).
        groups.a_traiter.push(sim);
      } else {
        groups.in_progress.push(sim);
      }
    });

    return groups;
  }, [listSimulations]);

  // Get pending simulations (no admin decision yet)
  const pendingSimulations = useMemo(() => {
    return simulations.filter(s => !s.admin_status);
  }, [simulations]);

  // Export de la liste filtrée + compteurs par statut (Tableau de bord Simulation)
  const [exportFilteredLoading, setExportFilteredLoading] = useState(false);

  // Compteurs indépendants de l'onglet actif (totaux dans le contexte de filtres courant)
  const simCounts = useMemo(() => {
    const biz = (s) => s.business_status || 'in_progress';
    return {
      aTraiter: filteredSimulations.filter(isATraiter).length,
      enCours: filteredSimulations.filter((s) => biz(s) === 'in_progress').length,
      signees: filteredSimulations.filter((s) => biz(s) === 'signed' || biz(s) === 'won').length,
      perdues: filteredSimulations.filter((s) => biz(s) === 'lost' || biz(s) === 'nogo').length,
    };
  }, [filteredSimulations]);

  const aTraiterTotal = useMemo(() => simulations.filter(isATraiter).length, [simulations]);

  const handleExportFiltered = async () => {
    setExportFilteredLoading(true);
    try {
      const ids = listSimulations.map(s => s.id);
      const response = await api().post('/simulations/export-selection', { ids }, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `CAPEX_SIMULATIONS_${new Date().toISOString().slice(0, 10)}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success(language === 'fr' ? 'Export téléchargé' : 'Export downloaded');
    } catch (error) {
      console.error('Export failed:', error);
      toast.error(language === 'fr' ? "Erreur lors de l'export" : 'Export failed');
    } finally {
      setExportFilteredLoading(false);
    }
  };

  // Calculate monthly trends
  const monthlyTrends = useMemo(() => {
    if (simulations.length === 0) return [];
    
    const months = [];
    for (let i = 5; i >= 0; i--) {
      const date = subMonths(new Date(), i);
      const monthStart = startOfMonth(date);
      const monthKey = format(monthStart, 'yyyy-MM');
      months.push({
        key: monthKey,
        label: format(monthStart, 'MMM yyyy', { locale: dateLocale }),
        viable: 0,
        risky: 0,
        nonViable: 0,
        total: 0,
      });
    }
    
    simulations.forEach(sim => {
      const simDate = parseISO(sim.created_at);
      const simMonthKey = format(simDate, 'yyyy-MM');
      const monthData = months.find(m => m.key === simMonthKey);
      if (monthData) {
        monthData.total++;
        if (sim.status === 'viable') monthData.viable++;
        else if (sim.status === 'risky') monthData.risky++;
        else monthData.nonViable++;
      }
    });
    
    return months;
  }, [simulations, dateLocale]);

  // Pie chart data
  const statusDistribution = useMemo(() => {
    if (stats.total === 0) return [];
    return [
      { name: 'Viable', value: stats.viable, color: COLORS.viable },
      { name: 'Risqué', value: stats.risky, color: COLORS.risky },
      { name: 'Non viable', value: stats.nonViable, color: COLORS['non-viable'] },
    ].filter(d => d.value > 0);
  }, [stats]);

  const successRate = stats.total > 0 ? ((stats.viable / stats.total) * 100).toFixed(0) : 0;

  // Handle status update (GO / NO GO / À L'ÉTUDE)
  const handleStatusUpdate = async () => {
    if (!selectedSimulation || !statusAction) return;
    
    setActionLoading(true);
    try {
      await api().put(`/simulations/${selectedSimulation.id}/status`, {
        status: statusAction,
        comment: statusComment
      });
      
      // Update local state
      setSimulations(prev => prev.map(s => 
        s.id === selectedSimulation.id 
          ? { ...s, admin_status: statusAction } 
          : s
      ));
      
      // Update stats
      setStats(prev => ({
        ...prev,
        pending: prev.pending - 1,
        approved: statusAction === 'approved' ? prev.approved + 1 : prev.approved,
        rejected: statusAction === 'rejected' ? prev.rejected + 1 : prev.rejected,
        pendingReview: statusAction === 'pending_review' ? prev.pendingReview + 1 : prev.pendingReview,
      }));
      
      toast.success(
        statusAction === 'approved' 
          ? (language === 'fr' ? 'Demande validée (GO)' : 'Request approved (GO)')
          : statusAction === 'rejected'
          ? (language === 'fr' ? 'Demande refusée (NO GO)' : 'Request rejected (NO GO)')
          : (language === 'fr' ? 'Analyse demandée' : 'Analysis requested')
      );
      
      setStatusDialogOpen(false);
      setSelectedSimulation(null);
      setStatusAction('');
      setStatusComment('');
    } catch (error) {
      console.error('Failed to update status:', error);
      toast.error(language === 'fr' ? 'Erreur lors de la mise à jour' : 'Failed to update');
    } finally {
      setActionLoading(false);
    }
  };

  const openStatusDialog = (sim, action) => {
    setSelectedSimulation(sim);
    setStatusAction(action);
    setStatusComment('');
    setStatusDialogOpen(true);
  };

  // Handle delete simulation
  const openDeleteDialog = (sim) => {
    setSimulationToDelete(sim);
    setDeleteDialogOpen(true);
  };

  // Handle export simulations list
  const handleExportList = async () => {
    setExportLoading(true);
    try {
      const response = await api().get('/admin/export-simulations', { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `CAPEX_LISTE_${new Date().toISOString().slice(0,10)}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success(language === 'fr' ? 'Export téléchargé' : 'Export downloaded');
    } catch (error) {
      console.error('Export failed:', error);
      toast.error(language === 'fr' ? 'Erreur lors de l\'export' : 'Export failed');
    } finally {
      setExportLoading(false);
    }
  };

  // Handle send reminders
  const handleSendReminders = async () => {
    setReminderLoading(true);
    try {
      const response = await api().post('/admin/send-reminders');
      toast.success(response.data.message);
    } catch (error) {
      console.error('Reminder failed:', error);
      toast.error(language === 'fr' ? 'Erreur lors de l\'envoi' : 'Send failed');
    } finally {
      setReminderLoading(false);
    }
  };

  // Handle recalculate all simulations
  const handleRecalculate = async () => {
    if (!window.confirm(language === 'fr' 
      ? 'Recalculer toutes les simulations avec les taux de cluster actuels ?' 
      : 'Recalculate all simulations with current cluster rates?')) return;
    setRecalcLoading(true);
    try {
      const response = await api().post('/admin/recalculate-all-simulations');
      const data = response.data;
      if (data.errors && data.errors.length > 0) {
        toast.warning(`${data.updated_count}/${data.total_simulations} recalculé(s). ${data.errors.length} ignoré(s).`);
      } else {
        toast.success(data.message);
      }
      const refreshed = await api().get('/simulations');
      setSimulations(refreshed.data);
    } catch (error) {
      console.error('Recalculate failed:', error);
      const msg = error.response?.data?.detail || (language === 'fr' ? 'Erreur lors du recalcul' : 'Recalculation failed');
      toast.error(msg);
    } finally {
      setRecalcLoading(false);
    }
  };

  const handleDeleteSimulation = async () => {
    if (!simulationToDelete) return;
    
    setDeleteLoading(true);
    try {
      await api().delete(`/simulations/${simulationToDelete.id}`);
      
      // Update local state
      setSimulations(prev => prev.filter(s => s.id !== simulationToDelete.id));
      
      // Update stats
      setStats(prev => ({
        ...prev,
        total: prev.total - 1,
        pending: simulationToDelete.admin_status ? prev.pending : prev.pending - 1,
        approved: simulationToDelete.admin_status === 'approved' ? prev.approved - 1 : prev.approved,
        rejected: simulationToDelete.admin_status === 'rejected' ? prev.rejected - 1 : prev.rejected,
      }));
      
      toast.success(language === 'fr' ? 'Analyse supprimée avec succès' : 'Analysis deleted successfully');
      setDeleteDialogOpen(false);
      setSimulationToDelete(null);
    } catch (error) {
      console.error('Failed to delete simulation:', error);
      toast.error(language === 'fr' ? 'Erreur lors de la suppression' : 'Failed to delete');
    } finally {
      setDeleteLoading(false);
    }
  };

  // Handle send to HG - opens modal first to confirm contract attachments
  const [hgModalSimId, setHgModalSimId] = useState(null);
  const [hgModalPreview, setHgModalPreview] = useState(null);
  const [hgModalIncludeContracts, setHgModalIncludeContracts] = useState(true);
  const [hgModalNote, setHgModalNote] = useState('');
  const [hgModalHistory, setHgModalHistory] = useState([]);
  const [hgModalHistoryOpen, setHgModalHistoryOpen] = useState(false);

  const openHGModal = async (simId) => {
    try {
      const [pv, hist] = await Promise.all([
        api().get(`/simulations/${simId}/hg-preview`),
        api().get(`/simulations/${simId}/hg-history`).catch(() => ({ data: { items: [] } })),
      ]);
      setHgModalPreview(pv.data);
      setHgModalIncludeContracts(pv.data.has_contract_attachments);
      setHgModalNote('');
      setHgModalHistory(hist.data.items || []);
      setHgModalHistoryOpen(false);
      setHgModalSimId(simId);
    } catch (e) {
      toast.error(language === 'fr' ? "Impossible de charger l'aperçu HG" : "Unable to load HG preview");
    }
  };

  const handleSendToHg = async (simId) => {
    setSendingToHg(simId);
    try {
      const response = await api().post(`/simulations/${simId}/send-to-hg`, {
        include_contract_attachments: hgModalIncludeContracts,
        note: hgModalNote || null,
      });
      const cnt = response.data.contract_attachments_count || 0;
      const baseMsg = language === 'fr'
        ? 'Dossier envoyé avec succès aux destinataires HG'
        : 'Case successfully sent to HG recipients';
      const extra = cnt > 0
        ? (language === 'fr' ? ` (+ ${cnt} contrat(s) joint(s))` : ` (+ ${cnt} contract(s) attached)`)
        : '';
      toast.success(baseMsg + extra);
      // Refresh local sim hg_history count for the badge
      try {
        const h = await api().get(`/simulations/${simId}/hg-history`);
        const items = h.data.items || [];
        setSimulations(prev => prev.map(s => s.id === simId
          ? { ...s, hg_history: items, hg_send_count: items.length }
          : s));
      } catch { /* silent */ }
      setHgModalSimId(null);
    } catch (error) {
      console.error('Failed to send to HG:', error);
      const msg = error.response?.data?.detail || (language === 'fr' ? 'Erreur lors de l\'envoi' : 'Failed to send');
      toast.error(msg);
    } finally {
      setSendingToHg(null);
    }
  };

  // ===== NON-ADMIN DASHBOARD (or Standard User View mode) =====
  if (!effectiveIsAdmin) {
    return (
      <AppLayout>
        <div className="space-y-6 animate-fade-in" data-testid="user-dashboard-page">
          {/* Header with Standard User View toggle */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-slate-100 dark:bg-slate-800 text-slate-500 dark:text-slate-400 text-xs font-medium mb-2">
                <CalendarDays className="w-3.5 h-3.5 text-orange-500" />
                {new Date().toLocaleDateString(language === 'fr' ? 'fr-FR' : 'en-US', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
              </div>
              <h1 className="text-2xl sm:text-3xl font-heading font-bold text-slate-900 dark:text-white">
                {(new Date().getHours() < 18 ? (language === 'fr' ? 'Bonjour' : 'Good morning') : (language === 'fr' ? 'Bonsoir' : 'Good evening'))} {user?.name} <span className="inline-block">👋</span>
              </h1>
              <p className="text-slate-500 dark:text-slate-400 mt-1">
                {language === 'fr' 
                  ? 'Gérez vos analyses CAPEX'
                  : 'Manage your CAPEX analyses'}
              </p>
            </div>
            <div className="flex flex-col sm:flex-row gap-3">
              <WeatherWidget />
              <CommercialWeatherWidget />
            </div>
            <div className="flex items-center gap-3">
              {/* Show toggle button only for admins in standard user view */}
              {isAdmin && isStandardUserView && (
                <Button 
                  onClick={() => setIsStandardUserView(false)}
                  variant="outline"
                  className="gap-2 border-indigo-300 text-indigo-700 hover:bg-indigo-50"
                  data-testid="switch-to-admin-view-btn"
                >
                  <UserCheck className="w-4 h-4" />
                  {language === 'fr' ? 'Vue Admin' : 'Admin View'}
                </Button>
              )}
              <Button 
                onClick={() => navigate('/simulation/new')} 
                className="gap-2 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700"
                size="lg"
                data-testid="create-simulation-btn"
              >
                <PlusCircle className="w-5 h-5" />
                {language === 'fr' ? 'Nouvelle Demande CAPEX' : 'New CAPEX Request'}
              </Button>
            </div>
          </div>

          {view === 'main' && (<>
          {/* Vue d'ensemble (KPIs) — juste sous la météo */}
          <div className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 shadow-sm overflow-hidden grid grid-cols-2 md:grid-cols-4 divide-x divide-y md:divide-y-0 divide-slate-100 dark:divide-slate-800" data-testid="user-kpi-strip">
            {[
              { key: 'total', label: language === 'fr' ? 'Créées' : 'Created', value: stats.total, color: 'text-slate-900 dark:text-white', dot: 'bg-blue-500' },
              { key: 'approved', label: language === 'fr' ? 'Validées (GO)' : 'Approved (GO)', value: stats.approved, color: 'text-emerald-600', dot: 'bg-emerald-500' },
              { key: 'pending', label: language === 'fr' ? 'En attente' : 'Pending', value: stats.pending + stats.pendingReview, color: 'text-amber-600', dot: 'bg-amber-500' },
              { key: 'rejected', label: language === 'fr' ? 'Refusées (NO GO)' : 'Rejected (NO GO)', value: stats.rejected, color: 'text-red-600', dot: 'bg-red-500' },
            ].map((c) => (
              <div key={c.key} className="px-5 py-4" data-testid={`stat-${c.key}`}>
                <div className="flex items-center gap-1.5">
                  <span className={`w-1.5 h-1.5 rounded-full ${c.dot}`} />
                  <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-500 truncate">{c.label}</p>
                </div>
                <p className={`mt-1 text-2xl sm:text-3xl font-semibold tabular-nums tracking-tight ${c.color}`}>{c.value}</p>
              </div>
            ))}
          </div>

          {/* Modules — gros boutons d'accès rapide aux sections */}
          <DashboardModules toProcessCount={aTraiterTotal} />

          {/* Personal Performance Pipeline */}
          {(() => {
            const signed = simulations.filter(s => s.business_status === 'signed' || s.business_status === 'won').length;
            const go = simulations.filter(s => s.admin_status === 'approved').length;
            const pending = simulations.filter(s => !s.admin_status || s.admin_status === 'pending_review' || s.admin_status === 'under_review').length;
            const total = simulations.length;
            const conversionRate = total > 0 ? Math.round((signed / total) * 100) : 0;
            const goRate = total > 0 ? Math.round((go / total) * 100) : 0;
            const totalCapexSigned = simulations
              .filter(s => s.business_status === 'signed' || s.business_status === 'won')
              .reduce((sum, s) => sum + (s.total_capex || 0), 0);
            const totalCASigned = simulations
              .filter(s => s.business_status === 'signed' || s.business_status === 'won')
              .reduce((sum, s) => sum + (s.ca_previsionnel_ht_proposed || s.average_ca_ht || 0), 0);
            const goWaiting30 = simulations.filter(s => {
              if (s.admin_status !== 'approved') return false;
              if (s.business_status === 'signed' || s.business_status === 'won') return false;
              const validatedAt = s.validated_at || s.created_at;
              if (!validatedAt) return false;
              const days = (Date.now() - new Date(validatedAt).getTime()) / (1000 * 60 * 60 * 24);
              return days > 30;
            });

            return (
              <div className="space-y-4">
                {/* Pipeline visuel */}
                <Card className="border border-indigo-200 bg-gradient-to-r from-indigo-50/50 to-blue-50/50" data-testid="user-pipeline">
                  <CardContent className="p-5">
                    <h3 className="text-sm font-semibold text-indigo-800 mb-4 flex items-center gap-2">
                      <TrendingUp className="w-4 h-4" />
                      {language === 'fr' ? 'Mon tunnel de conversion' : 'My conversion funnel'}
                    </h3>
                    <div className="flex items-center gap-1">
                      {[
                        { label: 'Études', count: total, color: 'bg-slate-500', width: '100%' },
                        { label: 'GO', count: go, color: 'bg-blue-500', width: total > 0 ? `${(go/total)*100}%` : '0%' },
                        { label: 'Signés', count: signed, color: 'bg-emerald-500', width: total > 0 ? `${(signed/total)*100}%` : '0%' },
                      ].map((step, i) => (
                        <div key={i} className="flex-1 text-center">
                          <div className={`${step.color} h-10 rounded-lg flex items-center justify-center text-white font-bold text-lg mb-1`} style={{ opacity: 0.2 + (0.8 * (step.count / Math.max(total, 1))) }}>
                            {step.count}
                          </div>
                          <span className="text-xs text-slate-500">{step.label}</span>
                        </div>
                      ))}
                    </div>
                    <div className="flex items-center justify-between mt-4 pt-3 border-t border-indigo-100">
                      <div className="text-center">
                        <p className="text-2xl font-bold text-indigo-700">{goRate}%</p>
                        <p className="text-[11px] text-slate-500">Taux GO</p>
                      </div>
                      <div className="text-center">
                        <p className="text-2xl font-bold text-emerald-700">{conversionRate}%</p>
                        <p className="text-[11px] text-slate-500">Taux signature</p>
                      </div>
                      <div className="text-center">
                        <p className="text-2xl font-bold text-blue-700">{totalCASigned > 0 ? `${(totalCASigned/1000).toFixed(0)}k` : '0'}€</p>
                        <p className="text-[11px] text-slate-500">CA Signé</p>
                      </div>
                      <div className="text-center">
                        <p className="text-2xl font-bold text-amber-700">{totalCapexSigned > 0 ? `${(totalCapexSigned/1000).toFixed(0)}k` : '0'}€</p>
                        <p className="text-[11px] text-slate-500">CAPEX engagé</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Alertes personnalisées */}
                {goWaiting30.length > 0 && (
                  <Card className="border-2 border-amber-300 bg-amber-50" data-testid="user-alerts">
                    <CardContent className="p-4">
                      <div className="flex items-start gap-3">
                        <AlertTriangle className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" />
                        <div>
                          <p className="font-semibold text-amber-800 text-sm">
                            {goWaiting30.length} dossier{goWaiting30.length > 1 ? 's' : ''} GO en attente de signature depuis +30 jours
                          </p>
                          <div className="mt-2 space-y-1">
                            {goWaiting30.slice(0, 3).map(s => (
                              <button
                                key={s.id}
                                onClick={() => navigate(`/simulation/${s.id}`)}
                                className="block text-sm text-amber-700 hover:text-amber-900 underline"
                              >
                                {s.project_name} ({s.retailer_name}) <VitrineBadge format={s.format || s.input_data?.general_info?.format} testid={`vitrine-badge-dash-${s.id}`} />
                              </button>
                            ))}
                            {goWaiting30.length > 3 && (
                              <p className="text-xs text-amber-500">+ {goWaiting30.length - 3} autre{goWaiting30.length - 3 > 1 ? 's' : ''}</p>
                            )}
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                )}
              </div>
            );
          })()}
          <GoldenInfoWidget language={language} />
          </>)}

          {/* Tableau de bord Simulation — recherche + liste (Mon Historique) */}
          {view === 'simulations' && (<>
          <SimulationStatusBar counts={simCounts} onExport={handleExportFiltered} exporting={exportFilteredLoading} active={activeTab} onSelect={setActiveTab} />
          {/* Search Bar + Mobile Filter button */}
          <Card className="border border-slate-200 dark:border-slate-800">
            <CardContent className="p-4">
              <div className="flex flex-col sm:flex-row gap-3">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-slate-400" />
                  <Input
                    type="text"
                    placeholder={language === 'fr' ? 'Rechercher par nom de projet, adresse, type...' : 'Search by project name, address, type...'}
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-10 h-12 text-base"
                    data-testid="search-input"
                  />
                </div>
                {commercialNames.length > 1 && (
                  <Select value={filterCommercial} onValueChange={setFilterCommercial}>
                    <SelectTrigger className="hidden sm:flex h-12 sm:w-64" data-testid="filter-commercial-select">
                      <SelectValue placeholder={language === 'fr' ? 'Tous les commerciaux' : 'All commercials'} />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">
                        {language === 'fr' ? `Tous les commerciaux (${commercialNames.length})` : `All commercials (${commercialNames.length})`}
                      </SelectItem>
                      {commercialNames.map((name) => (
                        <SelectItem key={name} value={name}>{name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
                {/* Mobile Filter button — opens bottom-sheet */}
                <Sheet open={mobileFilterOpen} onOpenChange={setMobileFilterOpen}>
                  <SheetTrigger asChild>
                    <Button
                      variant="outline"
                      className="sm:hidden h-12 gap-2"
                      data-testid="mobile-filter-btn"
                    >
                      <SlidersHorizontal className="w-4 h-4" />
                      {language === 'fr' ? 'Filtres' : 'Filters'}
                      {(filterBusinessStatus !== 'all' || filterCommercial !== 'all' || filterHasComments) && (
                        <Badge variant="secondary" className="ml-1 bg-blue-100 text-blue-700">!</Badge>
                      )}
                    </Button>
                  </SheetTrigger>
                  <SheetContent side="bottom" className="rounded-t-2xl max-h-[85vh] overflow-y-auto" data-testid="mobile-filter-sheet">
                    <SheetHeader className="text-left">
                      <SheetTitle>{language === 'fr' ? 'Filtres' : 'Filters'}</SheetTitle>
                    </SheetHeader>
                    <div className="space-y-5 mt-4 pb-6">
                      {/* Statut */}
                      <div>
                        <p className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2">
                          {language === 'fr' ? 'Statut commercial' : 'Business status'}
                        </p>
                        <div className="grid grid-cols-2 gap-2">
                          {[
                            { v: 'all', l: language === 'fr' ? `Tous (${simulations.length})` : `All (${simulations.length})`, c: '' },
                            { v: 'in_progress', l: language === 'fr' ? 'En cours' : 'In Progress', c: 'bg-blue-600' },
                            { v: 'won', l: language === 'fr' ? 'Gagnés' : 'Won', c: 'bg-emerald-600' },
                            { v: 'lost', l: language === 'fr' ? 'Perdus' : 'Lost', c: 'bg-red-600' },
                          ].map((opt) => (
                            <Button
                              key={opt.v}
                              variant={filterBusinessStatus === opt.v ? 'default' : 'outline'}
                              size="sm"
                              onClick={() => setFilterBusinessStatus(opt.v)}
                              className={filterBusinessStatus === opt.v ? `${opt.c} text-white` : ''}
                              data-testid={`mobile-filter-status-${opt.v}`}
                            >
                              {opt.l}
                            </Button>
                          ))}
                        </div>
                      </div>
                      {/* Commercial */}
                      {commercialNames.length > 1 && (
                        <div>
                          <p className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2">
                            {language === 'fr' ? 'Commercial' : 'Commercial'}
                          </p>
                          <Select value={filterCommercial} onValueChange={setFilterCommercial}>
                            <SelectTrigger className="h-11" data-testid="mobile-filter-commercial">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="all">
                                {language === 'fr' ? `Tous (${commercialNames.length})` : `All (${commercialNames.length})`}
                              </SelectItem>
                              {commercialNames.map((name) => (
                                <SelectItem key={name} value={name}>{name}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      )}
                      {/* With comments quick toggle */}
                      <div>
                        <p className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2">
                          {language === 'fr' ? 'Annotations' : 'Annotations'}
                        </p>
                        <Button
                          variant={filterHasComments ? 'default' : 'outline'}
                          size="sm"
                          onClick={() => setFilterHasComments((v) => !v)}
                          className={filterHasComments ? 'bg-blue-600 text-white gap-1.5' : 'gap-1.5'}
                          data-testid="mobile-filter-has-comments"
                        >
                          <MessageSquare className="w-4 h-4" />
                          {language === 'fr' ? 'Avec commentaires CAPEX' : 'With CAPEX comments'}
                        </Button>
                      </div>
                      {/* Apply / Reset buttons */}
                      <div className="flex gap-2 pt-2">
                        <Button
                          variant="outline"
                          className="flex-1"
                          onClick={() => { setFilterBusinessStatus('all'); setFilterCommercial('all'); setFilterHasComments(false); }}
                          data-testid="mobile-filter-reset"
                        >
                          {language === 'fr' ? 'Réinitialiser' : 'Reset'}
                        </Button>
                        <Button
                          className="flex-1 bg-blue-600 hover:bg-blue-700"
                          onClick={() => setMobileFilterOpen(false)}
                          data-testid="mobile-filter-apply"
                        >
                          {language === 'fr' ? 'Appliquer' : 'Apply'}
                        </Button>
                      </div>
                    </div>
                  </SheetContent>
                </Sheet>
              </div>
            </CardContent>
          </Card>

          {/* My Simulations List */}
          <Card className="border border-slate-200 dark:border-slate-800">
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between flex-wrap gap-4">
                <CardTitle className="text-lg flex items-center gap-2">
                  <Clock className="w-5 h-5 text-slate-500" />
                  {language === 'fr' ? 'Mon Historique' : 'My History'}
                </CardTitle>
                
                {/* Business Status Filter (desktop inline; mobile uses bottom-sheet) */}
                <div className="hidden sm:flex gap-2 flex-wrap">
                  <Button
                    variant={filterBusinessStatus === 'all' ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setFilterBusinessStatus('all')}
                    className="gap-1"
                  >
                    {language === 'fr' ? 'Tous' : 'All'}
                    <Badge variant="secondary" className="ml-1">{simulations.length}</Badge>
                  </Button>
                  <Button
                    variant={filterBusinessStatus === 'in_progress' ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setFilterBusinessStatus('in_progress')}
                    className={`gap-1 ${filterBusinessStatus === 'in_progress' ? 'bg-blue-600 hover:bg-blue-700' : ''}`}
                  >
                    <Clock className="w-3.5 h-3.5" />
                    {language === 'fr' ? 'En cours' : 'In Progress'}
                  </Button>
                  <Button
                    variant={filterBusinessStatus === 'won' ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setFilterBusinessStatus('won')}
                    className={`gap-1 ${filterBusinessStatus === 'won' ? 'bg-emerald-600 hover:bg-emerald-700' : ''}`}
                  >
                    <CheckCircle2 className="w-3.5 h-3.5" />
                    {language === 'fr' ? 'Gagnés' : 'Won'}
                  </Button>
                  <Button
                    variant={filterBusinessStatus === 'lost' ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setFilterBusinessStatus('lost')}
                    className={`gap-1 ${filterBusinessStatus === 'lost' ? 'bg-red-600 hover:bg-red-700' : ''}`}
                  >
                    <XCircle className="w-3.5 h-3.5" />
                    {language === 'fr' ? 'Perdus' : 'Lost'}
                  </Button>
                  <Button
                    variant={filterHasComments ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setFilterHasComments((v) => !v)}
                    className={`gap-1 ${filterHasComments ? 'bg-blue-600 hover:bg-blue-700' : ''}`}
                    title={language === 'fr' ? 'Filtrer les dossiers avec commentaires CAPEX' : 'Filter sims with CAPEX comments'}
                    data-testid="filter-has-comments"
                  >
                    <MessageSquare className="w-3.5 h-3.5" />
                    {language === 'fr' ? 'Annotés' : 'Annotated'}
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
                </div>
              ) : listSimulations.length === 0 ? (
                <div className="text-center py-12">
                  <FileText className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                  <p className="text-slate-500 dark:text-slate-400">
                    {language === 'fr' ? 'Aucune demande CAPEX' : 'No CAPEX requests yet'}
                  </p>
                  <Button 
                    onClick={() => navigate('/simulation/new')} 
                    className="mt-4 gap-2"
                    variant="outline"
                  >
                    <PlusCircle className="w-4 h-4" />
                    {language === 'fr' ? 'Créer ma première demande' : 'Create my first request'}
                  </Button>
                </div>
              ) : (
                <div className="space-y-3">
                  {listSimulations.map((sim) => (
                    <div
                      key={sim.id}
                      onClick={() => navigate(`/simulation/${sim.id}`)}
                      className={`p-4 rounded-lg border cursor-pointer transition-all hover:shadow-md ${
                        sim.admin_status === 'approved' 
                          ? 'border-emerald-200 bg-emerald-50/50' 
                          : sim.admin_status === 'rejected'
                          ? 'border-red-200 bg-red-50/50'
                          : 'border-slate-200 hover:border-blue-300'
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex-1 min-w-0">
                          <p className="font-semibold text-slate-900 dark:text-white text-sm leading-snug">
                            {getSimulationDisplayName(sim)} <VitrineBadge format={sim.format || sim.input_data?.general_info?.format} testid={`vitrine-badge-dash-${sim.id}`} />
                          </p>
                          <div className="flex items-center gap-1.5 mt-1 flex-wrap">
                            <div onClick={(e) => e.stopPropagation()}>
                              <BusinessStatusBadge 
                                simulation={sim} 
                                language={language}
                                editable={canChangeBusinessStatus}
                                onStatusChange={handleBusinessStatusChange}
                                isLoading={businessStatusLoading === sim.id}
                              />
                            </div>
                            <VerdictBadge 
                              verdict={sim.verdict} 
                              adminStatus={sim.admin_status} 
                              businessStatus={sim.business_status}
                              counterProposalPending={sim.admin_counter_proposal && !sim.counter_proposal_response}
                            />
                            {sim.capex_comments_count > 0 && (
                              <span
                                className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] font-semibold bg-blue-50 text-blue-700 border border-blue-200"
                                title={language === 'fr' ? `${sim.capex_comments_count} commentaire(s) CAPEX` : `${sim.capex_comments_count} CAPEX comment(s)`}
                                data-testid={`capex-comments-badge-${sim.id}`}
                              >
                                <MessageSquare className="w-3 h-3" />
                                {sim.capex_comments_count}
                              </span>
                            )}
                            {sim.chat_unread_count > 0 && (
                              <span
                                className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] font-bold bg-orange-500 text-white border border-orange-500 animate-pulse"
                                title={`${sim.chat_unread_count} message(s) non lu(s)`}
                                data-testid={`chat-unread-badge-${sim.id}`}
                              >
                                <MessageSquare className="w-3 h-3" />
                                {sim.chat_unread_count}
                              </span>
                            )}
                          </div>
                          <div className="flex items-center gap-4 mt-1.5 text-sm text-slate-500">
                            <span>{formatCurrency(sim.total_capex)}</span>
                            <span>•</span>
                            <span>
                              {safeFormatDistanceToNow(sim.created_at, { 
                                addSuffix: true, 
                                locale: dateLocale 
                              })}
                            </span>
                          </div>
                        </div>
                        <div className="flex items-center gap-1">
                          {(sim.business_status === 'signed' || sim.business_status === 'won') && (
                            <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
                              {renderAnnPicto(annStatusMap[sim.id], language, sim.id, () => { setAnnouncementSimId(sim.id); setShowAnnouncementModal(true); })}
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => { setAnnouncementSimId(sim.id); setShowAnnouncementModal(true); }}
                                className="h-8 px-2 text-white bg-emerald-500 hover:bg-emerald-600 gap-1"
                                title="Annonce ALL"
                                data-testid={`announce-all-btn-user-${sim.id}`}
                              >
                                <Megaphone className="w-4 h-4" />
                                <span className="hidden sm:inline text-xs">ALL</span>
                                {(annSentCountMap[sim.id] || 0) > 0 && (
                                  <span
                                    className="inline-flex items-center justify-center min-w-[1.1rem] h-4 px-1 rounded-full bg-white text-emerald-700 text-[10px] font-bold leading-none"
                                    title={language === 'fr' ? `Annonce ALL envoyée ${annSentCountMap[sim.id]} fois` : `ALL announcement sent ${annSentCountMap[sim.id]} times`}
                                    data-testid={`ann-sent-count-badge-${sim.id}`}
                                  >
                                    {annSentCountMap[sim.id]}
                                  </span>
                                )}
                              </Button>
                            </div>
                          )}
                          <ChevronRight className="w-5 h-5 text-slate-400 flex-shrink-0" />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
          </>)}
        </div>
        {/* Announcement ALL Modal (User view) */}
        <AnnouncementEditorModal
          open={showAnnouncementModal}
          onClose={() => { setShowAnnouncementModal(false); setAnnouncementSimId(null); }}
          simulationId={announcementSimId}
          token={token}
          onSuccess={() => { toast.success('Annonce soumise pour validation !'); refreshAnnStatuses(); }}
        />
        {renderBsPrompt()}
      </AppLayout>
    );
  }

  // ===== ADMIN / SUPER_ADMIN DASHBOARD =====
  
  // SimulationRow component for the grouped list
  const SimulationRow = ({ sim }) => (
    <div
      className={`p-3 rounded-lg border cursor-pointer transition-all hover:shadow-sm flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 ${
        sim.admin_status === 'approved' 
          ? 'border-emerald-200 bg-emerald-50/30' 
          : sim.admin_status === 'rejected'
          ? 'border-red-200 bg-red-50/30'
          : sim.admin_status === 'pending_review'
          ? 'border-amber-200 bg-amber-50/30'
          : 'border-slate-200 hover:border-blue-300'
      }`}
      onClick={() => navigate(`/simulation/${sim.id}`)}
    >
      <div className="flex items-center gap-4 flex-1 min-w-0">
        <div className="flex-1 min-w-0">
          <p className="font-semibold text-slate-900 dark:text-white text-sm leading-snug">
            {getSimulationDisplayName(sim)} <VitrineBadge format={sim.format || sim.input_data?.general_info?.format} testid={`vitrine-badge-dash-${sim.id}`} />
          </p>
          <div className="flex items-center gap-1.5 mt-1 flex-wrap">
            <div onClick={(e) => e.stopPropagation()}>
              <BusinessStatusBadge 
                simulation={sim} 
                language={language}
                editable={canChangeBusinessStatus}
                onStatusChange={handleBusinessStatusChange}
                isLoading={businessStatusLoading === sim.id}
              />
            </div>
            <VerdictBadge 
              verdict={sim.verdict} 
              adminStatus={sim.admin_status} 
              businessStatus={sim.business_status}
              counterProposalPending={sim.admin_counter_proposal && !sim.counter_proposal_response}
            />
            {sim.chat_unread_count > 0 && (
              <span
                className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] font-bold bg-orange-500 text-white border border-orange-500 animate-pulse"
                title={`${sim.chat_unread_count} message(s) non lu(s)`}
                data-testid={`chat-unread-badge-${sim.id}`}
              >
                <MessageSquare className="w-3 h-3" />
                {sim.chat_unread_count}
              </span>
            )}
          </div>
          <div className="flex items-center gap-2 flex-wrap text-xs text-slate-500 mt-1.5">
            <span>{safeFormatDate(sim.created_at, 'dd/MM/yyyy', dateLocale)}</span>
            <span>•</span>
            <span className="font-medium text-slate-700">{sim.creator_name || 'N/A'}</span>
            <span>•</span>
            <span>{formatCurrency(
              sim.admin_counter_proposal_ca || sim.ca_previsionnel_ht_proposed || sim.ca_potential_ht || 0
            )} HT</span>
            <span>•</span>
            <span>{((sim.admin_counter_proposal_commission || sim.commission_rate_proposed || sim.commission_rate_used || 0) * 100).toFixed(0)}%</span>
            <span>•</span>
            <span className={`font-medium ${sim.capex_carrier === 'CCD' ? 'text-blue-600' : sim.capex_carrier === 'CLIENT' ? 'text-amber-600' : 'text-purple-600'}`}>
              {sim.capex_carrier || 'N/A'}
            </span>
            {sim.commercial_comment && (
              <>
                <span>•</span>
                <span className="flex items-center gap-1 text-purple-600" title={sim.commercial_comment}>
                  <MessageSquare className="w-3 h-3" />
                  {language === 'fr' ? 'Commentaire' : 'Comment'}
                </span>
              </>
            )}
            {sim.validated && (
              <>
                <span>•</span>
                <span className="flex items-center gap-1 text-blue-600" title={sim.validated_at ? `Envoyé le ${safeFormatDate(sim.validated_at, 'dd/MM/yyyy à HH:mm', dateLocale)}` : 'Email envoyé à la Direction'}>
                  <Mail className="w-3 h-3" />
                  {language === 'fr' ? 'Envoyé' : 'Sent'}
                </span>
              </>
            )}
            {sim.contract_requested_at && (
              <>
                <span>•</span>
                <span 
                  className="flex items-center gap-1 text-emerald-600" 
                  title={`Contrat demandé le ${safeFormatDate(sim.contract_requested_at, 'dd/MM/yyyy à HH:mm', dateLocale)}`}
                >
                  <FileSignature className="w-3 h-3" />
                  {language === 'fr' ? 'Contrat' : 'Contract'}
                </span>
              </>
            )}
            {sim.has_contract_draft && !sim.contract_requested_at && (
              <>
                <span>•</span>
                <span 
                  className="flex items-center gap-1 text-amber-600"
                  title={language === 'fr' ? 'Brouillon de contrat sauvegardé' : 'Contract draft saved'}
                  data-testid={`draft-badge-${sim.id}`}
                >
                  <FileEdit className="w-3 h-3" />
                  {language === 'fr' ? 'Brouillon' : 'Draft'}
                </span>
              </>
            )}
          </div>
        </div>
      </div>
      
      {/* Quick actions for admin on non-decided items */}
      {canMakeDecision && !sim.admin_status && (
        <div className="flex flex-wrap items-center gap-1 ml-0 sm:ml-2 mt-2 sm:mt-0 justify-end" onClick={(e) => e.stopPropagation()}>
          <Button
            size="sm"
            variant="ghost"
            onClick={() => openStatusDialog(sim, 'approved')}
            className="h-8 w-8 p-0 text-emerald-600 hover:bg-emerald-100"
          >
            <ThumbsUp className="w-4 h-4" />
          </Button>
          <Button
            size="sm"
            variant="ghost"
            onClick={() => openStatusDialog(sim, 'pending_review')}
            className="h-8 w-8 p-0 text-amber-600 hover:bg-amber-100"
          >
            <HelpCircle className="w-4 h-4" />
          </Button>
          <Button
            size="sm"
            variant="ghost"
            onClick={() => openStatusDialog(sim, 'rejected')}
            className="h-8 w-8 p-0 text-red-600 hover:bg-red-100"
          >
            <ThumbsDown className="w-4 h-4" />
          </Button>
          {canDeleteSimulation && (
            <Button
              size="sm"
              variant="ghost"
              onClick={() => openDeleteDialog(sim)}
              className="h-8 w-8 p-0 text-red-600 hover:bg-red-100"
              title={language === 'fr' ? 'Supprimer' : 'Delete'}
            >
              <Trash2 className="w-4 h-4" />
            </Button>
          )}
        </div>
      )}
      
      {/* Actions for admin on decided items */}
      {effectiveIsAdmin && sim.admin_status && (
        <div className="flex flex-wrap items-center gap-1 ml-0 sm:ml-2 mt-2 sm:mt-0 justify-end" onClick={(e) => e.stopPropagation()}>
          {/* Send to HG button for won/signed simulations */}
          {canSendToHg && (sim.business_status === 'won' || sim.business_status === 'signed') && (
            <Button
              size="sm"
              variant="ghost"
              onClick={() => openHGModal(sim.id)}
              disabled={sendingToHg === sim.id}
              className="h-8 px-2 text-blue-600 hover:bg-blue-100 gap-1"
              title={language === 'fr' ? 'Envoyer chez HG' : 'Send to HG'}
              data-testid={`send-hg-btn-${sim.id}`}
            >
              {sendingToHg === sim.id ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Send className="w-4 h-4" />
              )}
              <span className="hidden sm:inline text-xs">HG</span>
              {(sim.hg_send_count || sim.hg_history?.length || 0) > 0 && (
                <span
                  className="inline-flex items-center justify-center min-w-[1.1rem] h-4 px-1 rounded-full bg-amber-500 text-white text-[10px] font-bold leading-none"
                  title={language === 'fr'
                    ? `Déjà envoyé ${sim.hg_send_count || sim.hg_history?.length} fois à HG`
                    : `Already sent ${sim.hg_send_count || sim.hg_history?.length} times`}
                  data-testid={`hg-send-count-badge-${sim.id}`}
                >
                  {sim.hg_send_count || sim.hg_history?.length}
                </span>
              )}
            </Button>
          )}
          {canDeleteSimulation && (
            <Button
              size="sm"
              variant="ghost"
              onClick={() => openDeleteDialog(sim)}
              className="h-8 w-8 p-0 text-red-600 hover:bg-red-100"
              title={language === 'fr' ? 'Supprimer' : 'Delete'}
            >
              <Trash2 className="w-4 h-4" />
            </Button>
          )}
        </div>
      )}

      {/* Annonce ALL button - visible for ALL users when business_status is signed/won */}
      {(sim.business_status === 'signed' || sim.business_status === 'won') && (
        <div className="flex items-center gap-1 ml-1" onClick={(e) => e.stopPropagation()}>
          {renderAnnPicto(annStatusMap[sim.id], language, sim.id, () => { setAnnouncementSimId(sim.id); setShowAnnouncementModal(true); })}
          <Button
            size="sm"
            variant="ghost"
            onClick={() => { setAnnouncementSimId(sim.id); setShowAnnouncementModal(true); }}
            className="h-8 px-2 text-white bg-emerald-500 hover:bg-emerald-600 gap-1"
            title="Annonce ALL"
            data-testid={`announce-all-btn-${sim.id}`}
          >
            <Megaphone className="w-4 h-4" />
            <span className="hidden sm:inline text-xs">ALL</span>
            {(annSentCountMap[sim.id] || 0) > 0 && (
              <span
                className="inline-flex items-center justify-center min-w-[1.1rem] h-4 px-1 rounded-full bg-white text-emerald-700 text-[10px] font-bold leading-none"
                title={language === 'fr' ? `Annonce ALL envoyée ${annSentCountMap[sim.id]} fois` : `ALL announcement sent ${annSentCountMap[sim.id]} times`}
                data-testid={`ann-sent-count-badge-${sim.id}`}
              >
                {annSentCountMap[sim.id]}
              </span>
            )}
          </Button>
        </div>
      )}
      
      <ChevronRight className="w-5 h-5 text-slate-400 flex-shrink-0 ml-2" />
    </div>
  );

  // Bloc DESIGN (colonne kanban) d'un statut d'étude — en-tête coloré + liste scrollable.
  const StatusColumn = ({ title, Icon, accent, items, headerExtra, testid, emptyLabel }) => (
    <div className={`flex-1 min-w-[300px] rounded-2xl border ${accent.border} bg-slate-50/60 dark:bg-slate-900/40 flex flex-col`} data-testid={testid}>
      <div className={`px-3 py-3 border-b ${accent.border} rounded-t-2xl bg-white/70 dark:bg-slate-900/60`}>
        <div className="flex items-center gap-2">
          <span className={`w-2.5 h-2.5 rounded-full ${accent.dot}`} />
          <Icon className={`w-4 h-4 ${accent.icon}`} />
          <h3 className={`font-semibold text-sm ${accent.text}`}>{title}</h3>
          <span className={`ml-auto inline-flex items-center justify-center min-w-[1.5rem] h-6 px-2 rounded-full text-xs font-bold ${accent.chip}`}>{items.length}</span>
        </div>
        {headerExtra}
      </div>
      <div className="p-2 space-y-2 overflow-y-auto max-h-[560px] min-h-[110px]">
        {items.length === 0 ? (
          <p className="text-xs text-slate-400 py-8 text-center">{emptyLabel}</p>
        ) : items.map((sim) => <SimulationRow key={sim.id} sim={sim} />)}
      </div>
    </div>
  );


  return (
    <AppLayout>
      <div className="space-y-6 animate-fade-in" data-testid="admin-dashboard-page">
        {/* Header — maquette : date, salutation, bouton + météo à droite */}
        <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-5">
          <div className="min-w-0">
            <p className="text-[11px] font-bold uppercase tracking-[0.12em] text-label mb-1">
              {new Date().toLocaleDateString(language === 'fr' ? 'fr-FR' : 'en-US', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
            </p>
            <h1 className="font-display font-bold uppercase leading-[1.05] tracking-[0.01em] text-ink text-4xl sm:text-5xl">
              {(new Date().getHours() < 18 ? (language === 'fr' ? 'Bonjour' : 'Good morning') : (language === 'fr' ? 'Bonsoir' : 'Good evening'))} {user?.name}
            </h1>
            <div className="flex items-center gap-2 flex-wrap mt-4">
              <Button
                onClick={() => navigate('/simulation/new')}
                className="gap-2"
                data-testid="create-simulation-btn"
              >
                <PlusCircle className="w-5 h-5" />
                {language === 'fr' ? 'Nouvelle simulation' : 'New simulation'}
              </Button>
              <Button
                onClick={() => setIsStandardUserView(true)}
                variant="outline"
                size="sm"
                className="gap-2"
                data-testid="switch-to-user-view-btn"
              >
                <Eye className="w-4 h-4" />
                {language === 'fr' ? 'Vue Utilisateur' : 'User View'}
              </Button>
            </div>
          </div>
          {view === 'main' && (
            <div className="lg:flex-none" data-testid="dashboard-top-row">
              <CommercialWeatherWidget />
            </div>
          )}
        </div>

        {view === 'main' && (<>
        {/* Vue d'ensemble CAPEX (KPIs en cartes blanches) */}
        <KPIDashboard language={language} />

        {/* Modules — gros boutons d'accès rapide aux sections */}
        <DashboardModules toProcessCount={aTraiterTotal} />

        {/* Bandeaux + Control Room retirés de l'accueil (accessibles via les modules et le Tableau de bord Simulation) */}
        {false && (<>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4" data-testid="dashboard-pastel-banners">
          {[
            { key: 'to-validate', label: language === 'fr' ? 'À valider' : 'To validate', value: (stats.pendingReview || 0) + (stats.pending || 0), bg: 'bg-amber-50 dark:bg-amber-900/15', ring: 'ring-amber-100 dark:ring-amber-900/30', dot: 'bg-amber-400', text: 'text-amber-700 dark:text-amber-300' },
            { key: 'approved', label: language === 'fr' ? 'Approuvés' : 'Approved', value: stats.approved || 0, bg: 'bg-emerald-50 dark:bg-emerald-900/15', ring: 'ring-emerald-100 dark:ring-emerald-900/30', dot: 'bg-emerald-400', text: 'text-emerald-700 dark:text-emerald-300' },
            { key: 'rejected', label: language === 'fr' ? 'Refusés' : 'Rejected', value: stats.rejected || 0, bg: 'bg-rose-50 dark:bg-rose-900/15', ring: 'ring-rose-100 dark:ring-rose-900/30', dot: 'bg-rose-400', text: 'text-rose-700 dark:text-rose-300' },
            { key: 'total', label: language === 'fr' ? 'Simulations' : 'Simulations', value: simulations.length, bg: 'bg-indigo-50 dark:bg-indigo-900/15', ring: 'ring-indigo-100 dark:ring-indigo-900/30', dot: 'bg-indigo-400', text: 'text-indigo-700 dark:text-indigo-300' },
          ].map((b) => (
            <div key={b.key} className={`rounded-2xl ${b.bg} ring-1 ${b.ring} px-5 py-4 flex items-center justify-between`} data-testid={`pastel-${b.key}`}>
              <div className="flex items-center gap-2.5">
                <span className={`w-2.5 h-2.5 rounded-full ${b.dot}`} />
                <span className={`text-sm font-medium ${b.text}`}>{b.label}</span>
              </div>
              <span className={`text-2xl font-bold tabular-nums ${b.text}`}>{b.value}</span>
            </div>
          ))}
        </div>


        {/* Control Room: Action Center (left) + Secondary modules (right) */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-start">
          {/* LEFT — Action Center: pending validations (focal point) */}
          <div className="lg:col-span-8 space-y-6" data-testid="dashboard-action-center">
        {effectiveIsAdmin && pendingSimulations.length > 0 ? (
          <Card className="border-2 border-orange-300 bg-orange-50/30">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2 text-orange-700">
                <AlertTriangle className="w-5 h-5" />
                {language === 'fr' ? `Demandes en attente de validation (${pendingSimulations.length})` : `Pending Requests (${pendingSimulations.length})`}
              </CardTitle>
              <CardDescription>
                {language === 'fr' ? 'Ces demandes nécessitent votre décision' : 'These requests need your decision'}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {pendingSimulations.slice(0, 5).map((sim) => (
                  <div
                    key={sim.id}
                    className="p-4 rounded-lg border border-orange-200 bg-white dark:bg-slate-900 hover:shadow-md transition-all"
                  >
                    <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 sm:gap-4">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <h3 className="font-semibold text-slate-900 dark:text-white">
                            {getSimulationDisplayName(sim)} <VitrineBadge format={sim.format || sim.input_data?.general_info?.format} testid={`vitrine-badge-dash-${sim.id}`} />
                          </h3>
                          <VerdictBadge 
                            verdict={sim.verdict} 
                            adminStatus={sim.admin_status} 
                            businessStatus={sim.business_status}
                            counterProposalPending={sim.admin_counter_proposal && !sim.counter_proposal_response}
                          />
                        </div>
                        <div className="flex items-center gap-x-3 gap-y-1 mt-2 text-sm text-slate-500 flex-wrap">
                          <span>{safeFormatDate(sim.created_at, 'dd/MM/yyyy', dateLocale)}</span>
                          <span>•</span>
                          <span className="flex items-center gap-1">
                            <User className="w-3 h-3" />
                            {sim.creator_name || 'N/A'}
                          </span>
                          <span>•</span>
                          <span>{formatCurrency(sim.admin_counter_proposal_ca || sim.ca_previsionnel_ht_proposed || sim.ca_potential_ht || 0)} HT</span>
                          <span>•</span>
                          <span>{((sim.admin_counter_proposal_commission || sim.commission_rate_proposed || sim.commission_rate_used || 0) * 100).toFixed(0)}%</span>
                          <span>•</span>
                          <span className={`font-medium ${sim.capex_carrier === 'CCD' ? 'text-blue-600' : sim.capex_carrier === 'CLIENT' ? 'text-amber-600' : 'text-purple-600'}`}>
                            {sim.capex_carrier || 'N/A'}
                          </span>
                          {sim.validated && (
                            <>
                              <span>•</span>
                              <span className="flex items-center gap-1 text-blue-600" title={sim.validated_at ? `Envoyé le ${safeFormatDate(sim.validated_at, 'dd/MM/yyyy à HH:mm', dateLocale)}` : 'Email envoyé'}>
                                <Mail className="w-3 h-3" />
                                Envoyé
                              </span>
                            </>
                          )}
                          {sim.contract_requested_at && (
                            <>
                              <span>•</span>
                              <span 
                                className="flex items-center gap-1 text-emerald-600" 
                                title={`Contrat demandé le ${safeFormatDate(sim.contract_requested_at, 'dd/MM/yyyy à HH:mm', dateLocale)}`}
                              >
                                <FileSignature className="w-3 h-3" />
                                Contrat
                              </span>
                            </>
                          )}
                          {sim.has_contract_draft && !sim.contract_requested_at && (
                            <>
                              <span>•</span>
                              <span 
                                className="flex items-center gap-1 text-amber-600"
                                title="Brouillon de contrat sauvegardé"
                                data-testid={`draft-badge-mobile-${sim.id}`}
                              >
                                <FileEdit className="w-3 h-3" />
                                Brouillon
                              </span>
                            </>
                          )}
                          <span>•</span>
                          <span>{sim.opening_date 
                            ? safeFormatDate(sim.opening_date, 'dd/MM/yyyy', dateLocale)
                            : 'NC'}</span>
                          <span>•</span>
                          <span>
                            {sim.red_months_count !== undefined ? `${sim.red_months_count}/12 mois rouges` : '-'}
                          </span>
                        </div>
                        {/* User Proposal Display */}
                        {sim.validated && sim.commission_rate_proposed && (
                          <div className="mt-3 p-3 rounded-lg bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800">
                            <p className="text-xs font-semibold text-blue-600 dark:text-blue-400 mb-2 flex items-center gap-1">
                              <CheckCircle2 className="w-3 h-3" />
                              {language === 'fr' ? 'PROPOSITION CLIENT SOUMISE' : 'CLIENT PROPOSAL SUBMITTED'}
                            </p>
                            <div className="grid grid-cols-2 gap-4 text-sm">
                              <div>
                                <p className="text-blue-600 dark:text-blue-400 text-xs">{language === 'fr' ? 'Commission proposée' : 'Proposed Commission'}</p>
                                <p className="font-bold text-blue-700 dark:text-blue-300 text-lg">
                                  {(sim.commission_rate_proposed * 100).toFixed(0)}%
                                </p>
                              </div>
                              <div>
                                <p className="text-blue-600 dark:text-blue-400 text-xs">{language === 'fr' ? 'CA Prévisionnel HT' : 'Projected Revenue'}</p>
                                <p className="font-bold text-blue-700 dark:text-blue-300 text-lg">
                                  {formatCurrency(sim.ca_previsionnel_ht_proposed || 0)}
                                </p>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                      
                      {/* Action Buttons */}
                      <div className="flex items-center gap-2 flex-wrap sm:flex-shrink-0 sm:justify-end -mx-1">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => {
                            console.log('Voir clicked, navigating to:', `/simulation/${sim.id}`);
                            navigate(`/simulation/${sim.id}`);
                          }}
                          className="gap-1"
                          data-testid={`view-btn-${sim.id}`}
                        >
                          <Eye className="w-4 h-4" />
                          {language === 'fr' ? 'Voir' : 'View'}
                        </Button>
                        <Button
                          size="sm"
                          variant="default"
                          onClick={(e) => {
                            e.stopPropagation();
                            openStatusDialog(sim, 'approved');
                          }}
                          className="gap-1 bg-emerald-600 hover:bg-emerald-700"
                          data-testid={`go-btn-${sim.id}`}
                        >
                          <ThumbsUp className="w-4 h-4" />
                          GO
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={(e) => {
                            e.stopPropagation();
                            openStatusDialog(sim, 'pending_review');
                          }}
                          className="gap-1 border-amber-400 text-amber-600 hover:bg-amber-50"
                          data-testid={`review-btn-${sim.id}`}
                        >
                          <HelpCircle className="w-4 h-4" />
                          {language === 'fr' ? 'Étude' : 'Review'}
                        </Button>
                        <Button
                          size="sm"
                          variant="destructive"
                          onClick={(e) => {
                            e.stopPropagation();
                            openStatusDialog(sim, 'rejected');
                          }}
                          className="gap-1"
                          data-testid={`nogo-btn-${sim.id}`}
                        >
                          <ThumbsDown className="w-4 h-4" />
                          NO GO
                        </Button>
                        {/* Reassign button - admin only */}
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={(e) => {
                            e.stopPropagation();
                            setReassignTarget(sim);
                          }}
                          className="gap-1 text-indigo-600 hover:text-indigo-700 hover:bg-indigo-50"
                          data-testid={`reassign-btn-${sim.id}`}
                          title={language === 'fr' ? 'Réaffecter à un commercial' : 'Reassign'}
                        >
                          <UserCog className="w-4 h-4" />
                          {language === 'fr' ? 'Réaffecter' : 'Reassign'}
                        </Button>
                        {/* Delete button - admin only */}
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={(e) => {
                            e.stopPropagation();
                            openDeleteDialog(sim);
                          }}
                          className="gap-1 text-red-600 hover:text-red-700 hover:bg-red-50"
                          data-testid={`delete-btn-${sim.id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                          {language === 'fr' ? 'Supprimer' : 'Delete'}
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
                
                {pendingSimulations.length > 5 && (
                  <Button 
                    variant="outline" 
                    className="w-full"
                    onClick={() => navigate('/history')}
                  >
                    {language === 'fr' ? `Voir les ${pendingSimulations.length - 5} autres demandes` : `View ${pendingSimulations.length - 5} more requests`}
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>
        ) : (
          <Card className="border border-slate-200 dark:border-slate-800" data-testid="no-pending-card">
            <CardContent className="p-10 text-center">
              <CheckCircle2 className="w-10 h-10 text-emerald-400 mx-auto mb-3" />
              <p className="text-sm font-medium text-slate-700 dark:text-slate-200">
                {language === 'fr' ? 'Aucune demande en attente de validation' : 'No requests pending validation'}
              </p>
              <p className="text-xs text-slate-400 mt-1">
                {language === 'fr' ? 'Tout est traité 👏' : 'All caught up'}
              </p>
            </CardContent>
          </Card>
        )}
          </div>

          {/* RIGHT — Secondary modules grouped in tabs (reduces vertical bloat) */}
          <div className="lg:col-span-4 space-y-4" data-testid="dashboard-secondary">
            <Tabs defaultValue="info" className="w-full">
              <TabsList className="grid w-full grid-cols-3">
                <TabsTrigger value="info" data-testid="tab-info-or">{language === 'fr' ? "Info d'Or" : 'Gold Info'}</TabsTrigger>
                <TabsTrigger value="contrats" data-testid="tab-contrats">{language === 'fr' ? 'Contrats' : 'Contracts'}</TabsTrigger>
                <TabsTrigger value="equipe" data-testid="tab-equipe">{language === 'fr' ? 'Équipe' : 'Team'}</TabsTrigger>
              </TabsList>
              <TabsContent value="info" className="mt-4">
                <GoldenInfoWidget language={language} />
              </TabsContent>
              <TabsContent value="contrats" className="mt-4">
                <ContractRequestsWidget language={language} />
              </TabsContent>
              <TabsContent value="equipe" className="mt-4">
                <UserKPIsTable
                  userKpis={calculatedUserKpis}
                  kpiPeriod={kpiPeriod}
                  setKpiPeriod={setKpiPeriod}
                  language={language}
                />
              </TabsContent>
            </Tabs>
          </div>
        </div>
        </>)}
        </>)}

        {/* Tableau de bord Simulation — toutes les demandes groupées */}
        {view === 'simulations' && (<>
        <SimulationStatusBar counts={simCounts} onExport={handleExportFiltered} exporting={exportFilteredLoading} active={activeTab} onSelect={setActiveTab} />
        <Card className="border border-slate-200 dark:border-slate-800" data-testid="simulation-dashboard-list">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg flex items-center gap-2">
                <FileText className="w-5 h-5 text-slate-500" />
                {language === 'fr' ? 'Toutes les demandes' : 'All Requests'}
                <Badge variant="secondary" className="ml-1">{listSimulations.length}</Badge>
              </CardTitle>
            </div>
            <div className="relative mt-4">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-slate-400" />
              <Input
                type="text"
                placeholder={language === 'fr' ? 'Rechercher par projet, créateur, type...' : 'Search by project, creator, type...'}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 h-11"
              />
            </div>
            {/* Filters Row */}
            <SimulationFilters
              filterStatus={filterStatus}
              setFilterStatus={setFilterStatus}
              filterCarrier={filterCarrier}
              setFilterCarrier={setFilterCarrier}
              filterCommercial={filterCommercial}
              setFilterCommercial={setFilterCommercial}
              filterBusinessStatus={filterBusinessStatus}
              setFilterBusinessStatus={setFilterBusinessStatus}
              sortBy={sortBy}
              setSortBy={setSortBy}
              commercialNames={commercialNames}
              language={language}
              onClear={() => { 
                setFilterStatus('all'); 
                setFilterCarrier('all'); 
                setFilterCommercial('all'); 
                setFilterBusinessStatus('all'); 
              }}
            />
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
              </div>
            ) : listSimulations.length === 0 ? (
              <div className="text-center py-12">
                <FileText className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                <p className="text-slate-500">{language === 'fr' ? 'Aucune demande trouvée' : 'No requests found'}</p>
              </div>
            ) : (
              <div className="space-y-6">
                <div className="flex gap-4 overflow-x-auto pb-3 -mx-1 px-1" data-testid="simulation-kanban">
                  <StatusColumn
                    testid="col-a-traiter"
                    title={language === 'fr' ? 'Étude à traiter (Direction)' : 'To review (Management)'}
                    Icon={Clock}
                    accent={{ dot: 'bg-amber-500', icon: 'text-amber-500', text: 'text-amber-700 dark:text-amber-300', border: 'border-amber-200 dark:border-amber-900/60', chip: 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300' }}
                    items={groupedSimulations.a_traiter}
                    emptyLabel={language === 'fr' ? 'Aucune étude à traiter.' : 'Nothing to review.'}
                  />
                  <StatusColumn
                    testid="col-en-cours"
                    title={language === 'fr' ? 'Étude En cours (GO)' : 'In progress (GO)'}
                    Icon={Send}
                    accent={{ dot: 'bg-blue-500', icon: 'text-blue-500', text: 'text-blue-700 dark:text-blue-300', border: 'border-blue-200 dark:border-blue-900/60', chip: 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300' }}
                    items={groupedSimulations.in_progress}
                    emptyLabel={language === 'fr' ? 'Aucune étude en cours.' : 'No study in progress.'}
                  />
                  {(() => {
                    const isDone = (sim) => (annSentCountMap[sim.id] || 0) > 0;
                    const doneCount = groupedSimulations.signed.filter(isDone).length;
                    const todoCount = groupedSimulations.signed.length - doneCount;
                    const signedList = groupedSimulations.signed.filter((sim) => {
                      if (annFilter === 'done') return isDone(sim);
                      if (annFilter === 'todo') return !isDone(sim);
                      return true;
                    });
                    const FilterBtn = ({ value, label }) => (
                      <button
                        type="button"
                        onClick={() => setAnnFilter(value)}
                        className={`px-2 py-0.5 rounded-full text-[11px] font-medium transition-colors ${annFilter === value ? 'bg-emerald-600 text-white shadow-sm' : 'bg-emerald-50 text-emerald-700 hover:bg-emerald-100 dark:bg-emerald-900/30 dark:text-emerald-300'}`}
                        data-testid={`ann-filter-${value}`}
                      >
                        {label}
                      </button>
                    );
                    return (
                      <StatusColumn
                        testid="col-signee"
                        title={language === 'fr' ? 'Étude Signée' : 'Signed'}
                        Icon={CheckCircle2}
                        accent={{ dot: 'bg-emerald-500', icon: 'text-emerald-500', text: 'text-emerald-700 dark:text-emerald-300', border: 'border-emerald-200 dark:border-emerald-900/60', chip: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300' }}
                        items={signedList}
                        emptyLabel={language === 'fr' ? 'Aucun dossier pour ce filtre.' : 'No dossier for this filter.'}
                        headerExtra={
                          <div className="flex flex-wrap items-center gap-1.5 mt-2" data-testid="ann-filter-group">
                            <FilterBtn value="all" label={language === 'fr' ? `Toutes (${groupedSimulations.signed.length})` : `All (${groupedSimulations.signed.length})`} />
                            <FilterBtn value="done" label={language === 'fr' ? `Faite (${doneCount})` : `Done (${doneCount})`} />
                            <FilterBtn value="todo" label={language === 'fr' ? `À faire (${todoCount})` : `To do (${todoCount})`} />
                          </div>
                        }
                      />
                    );
                  })()}
                  <StatusColumn
                    testid="col-perdu"
                    title={language === 'fr' ? 'Étude perdu (NO GO)' : 'Lost (NO GO)'}
                    Icon={XCircle}
                    accent={{ dot: 'bg-red-500', icon: 'text-red-500', text: 'text-red-700 dark:text-red-300', border: 'border-red-200 dark:border-red-900/60', chip: 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300' }}
                    items={[...groupedSimulations.nogo, ...groupedSimulations.lost]}
                    emptyLabel={language === 'fr' ? 'Aucune étude perdue.' : 'No lost study.'}
                  />
                </div>
                
                {listSimulations.length > 30 && (
                  <Button 
                    variant="outline" 
                    className="w-full mt-4"
                    onClick={() => navigate('/history')}
                  >
                    {language === 'fr' ? 'Voir tout l\'historique' : 'View full history'}
                    <ChevronRight className="w-4 h-4 ml-1" />
                  </Button>
                )}
              </div>
            )}
          </CardContent>
        </Card>
        </>)}

        {/* Charts Section — masqués sur l'accueil (Tendances / Répartition) à la demande */}
        {false && !loading && simulations.length > 0 && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Monthly Trends */}
            <Card className="border border-slate-200 dark:border-slate-800">
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <BarChart3 className="w-5 h-5 text-slate-500" />
                  {language === 'fr' ? 'Tendances Mensuelles' : 'Monthly Trends'}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={monthlyTrends}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                      <XAxis dataKey="label" tick={{ fontSize: 11 }} />
                      <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                      <Tooltip />
                      <Legend />
                      <Bar dataKey="viable" name="Viable" fill={COLORS.viable} radius={[4, 4, 0, 0]} />
                      <Bar dataKey="risky" name="Risqué" fill={COLORS.risky} radius={[4, 4, 0, 0]} />
                      <Bar dataKey="nonViable" name="Non viable" fill={COLORS['non-viable']} radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>

            {/* Status Distribution */}
            <Card className="border border-slate-200 dark:border-slate-800">
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <PieChart className="w-5 h-5 text-slate-500" />
                  {language === 'fr' ? 'Répartition des Statuts' : 'Status Distribution'}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="h-64 flex items-center justify-center">
                  {statusDistribution.length > 0 ? (
                    <ResponsiveContainer width="100%" height="100%">
                      <RechartsPieChart>
                        <Pie
                          data={statusDistribution}
                          cx="50%"
                          cy="50%"
                          innerRadius={60}
                          outerRadius={100}
                          paddingAngle={2}
                          dataKey="value"
                          label={({ name, value }) => `${name}: ${value}`}
                        >
                          {statusDistribution.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={entry.color} />
                          ))}
                        </Pie>
                        <Tooltip />
                      </RechartsPieChart>
                    </ResponsiveContainer>
                  ) : (
                    <p className="text-slate-400">{language === 'fr' ? 'Pas de données' : 'No data'}</p>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>
        )}
      </div>

      {/* Status Update Dialog */}
      <Dialog open={statusDialogOpen} onOpenChange={setStatusDialogOpen}>
        <DialogContent className="w-full sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>
              {statusAction === 'approved' && (language === 'fr' ? '✅ Valider la demande (GO)' : '✅ Approve Request (GO)')}
              {statusAction === 'rejected' && (language === 'fr' ? '❌ Refuser la demande (NO GO)' : '❌ Reject Request (NO GO)')}
              {statusAction === 'pending_review' && (language === 'fr' ? '⏳ Demander une analyse' : '⏳ Request Review')}
            </DialogTitle>
            <DialogDescription>
              {selectedSimulation && getSimulationDisplayName(selectedSimulation)}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <label className="text-sm font-medium text-slate-700 dark:text-slate-300 mb-2 block">
                {language === 'fr' ? 'Commentaire (optionnel)' : 'Comment (optional)'}
              </label>
              <Textarea
                value={statusComment}
                onChange={(e) => setStatusComment(e.target.value)}
                placeholder={language === 'fr' ? 'Ajoutez un commentaire...' : 'Add a comment...'}
                rows={3}
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setStatusDialogOpen(false)}>
              {language === 'fr' ? 'Annuler' : 'Cancel'}
            </Button>
            <Button 
              onClick={handleStatusUpdate}
              disabled={actionLoading}
              className={
                statusAction === 'approved' ? 'bg-emerald-600 hover:bg-emerald-700' :
                statusAction === 'rejected' ? 'bg-red-600 hover:bg-red-700' :
                'bg-amber-600 hover:bg-amber-700'
              }
            >
              {actionLoading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              {language === 'fr' ? 'Confirmer' : 'Confirm'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent className="w-full sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <Trash2 className="w-5 h-5" />
              {language === 'fr' ? 'Confirmer la suppression' : 'Confirm Deletion'}
            </DialogTitle>
            <DialogDescription>
              {language === 'fr' 
                ? `Êtes-vous sûr de vouloir supprimer l'analyse "${simulationToDelete ? getSimulationDisplayName(simulationToDelete) : ''}" ? Cette action est irréversible.`
                : `Are you sure you want to delete the analysis "${simulationToDelete ? getSimulationDisplayName(simulationToDelete) : ''}"? This action cannot be undone.`
              }
            </DialogDescription>
          </DialogHeader>
          
          <DialogFooter className="gap-2 sm:gap-0">
            <Button variant="outline" onClick={() => setDeleteDialogOpen(false)}>
              {language === 'fr' ? 'Annuler' : 'Cancel'}
            </Button>
            <Button 
              variant="destructive"
              onClick={handleDeleteSimulation}
              disabled={deleteLoading}
              className="gap-2"
            >
              {deleteLoading && <Loader2 className="w-4 h-4 animate-spin" />}
              <Trash2 className="w-4 h-4" />
              {language === 'fr' ? 'Supprimer' : 'Delete'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Announcement ALL Modal */}
      <AnnouncementEditorModal
        open={showAnnouncementModal}
        onClose={() => { setShowAnnouncementModal(false); setAnnouncementSimId(null); }}
        simulationId={announcementSimId}
        token={token}
        onSuccess={() => { toast.success('Annonce soumise pour validation !'); refreshAnnStatuses(); }}
      />

      <ReassignSimulationDialog
        sim={reassignTarget}
        open={!!reassignTarget}
        onOpenChange={(o) => !o && setReassignTarget(null)}
        onDone={(d) => {
          setSimulations(prev => prev.map(s => s.id === reassignTarget?.id
            ? { ...s, creator_name: d.creator_name, creator_email: d.creator_email }
            : s));
          setReassignTarget(null);
        }}
      />

      {/* HG Send Confirmation Modal */}
      <Dialog open={!!hgModalSimId} onOpenChange={(open) => !open && setHgModalSimId(null)}>
        <DialogContent className="max-w-lg" data-testid="hg-send-modal-dashboard">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-amber-700">
              <Send className="w-5 h-5" />
              {language === 'fr' ? 'Envoyer le dossier à HG' : 'Send dossier to HG'}
            </DialogTitle>
            <DialogDescription>
              {language === 'fr'
                ? "Vérifiez les pièces jointes avant l'envoi."
                : 'Review attachments before sending.'}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-2">
            {hgModalHistory.length > 0 && (
              <div className="bg-slate-50 rounded-md border border-slate-200">
                <button
                  type="button"
                  onClick={() => setHgModalHistoryOpen(v => !v)}
                  className="w-full flex items-center justify-between px-3 py-2 text-xs font-semibold text-slate-700 hover:bg-slate-100 rounded-md transition"
                  data-testid="hg-history-toggle-dashboard"
                >
                  <span className="flex items-center gap-2">
                    <Clock className="w-3.5 h-3.5 text-slate-500" />
                    {language === 'fr'
                      ? `Historique des envois HG (${hgModalHistory.length})`
                      : `HG send history (${hgModalHistory.length})`}
                  </span>
                  <span className="text-slate-400">{hgModalHistoryOpen ? '▾' : '▸'}</span>
                </button>
                {hgModalHistoryOpen && (
                  <div className="px-3 pb-3 pt-1 space-y-2 max-h-48 overflow-y-auto" data-testid="hg-history-list-dashboard">
                    {hgModalHistory.map(h => (
                      <div key={h.id} className="bg-white rounded border border-slate-200 p-2.5 text-xs">
                        <div className="flex items-start justify-between gap-2 mb-1">
                          <span className="font-semibold text-slate-700">{h.sent_by_name}</span>
                          <span className="text-slate-400 text-[10px] whitespace-nowrap">
                            {new Date(h.sent_at).toLocaleString('fr-FR', { dateStyle: 'short', timeStyle: 'short' })}
                          </span>
                        </div>
                        <div className="text-slate-500 text-[11px] mb-1">
                          → {(h.recipients || []).join(', ')}
                          {h.contract_attachments_count > 0 && (
                            <span className="ml-2 text-amber-600">+ {h.contract_attachments_count} contrat(s)</span>
                          )}
                        </div>
                        {h.note && (
                          <div className="mt-1 pl-2 border-l-2 border-amber-300 text-slate-600 italic whitespace-pre-wrap">
                            "{h.note}"
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            <div>
              <div className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">
                {language === 'fr' ? 'Destinataires' : 'Recipients'}
              </div>
              <div className="bg-slate-50 rounded-md p-3 space-y-1 text-sm">
                {(hgModalPreview?.recipients || []).map(r => (
                  <div key={r.email} className="flex items-center gap-2">
                    <span className="text-slate-700">📧 {r.email}</span>
                    {r.name && <span className="text-xs text-slate-400">— {r.name}</span>}
                  </div>
                ))}
              </div>
            </div>

            <div>
              <div className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">
                {language === 'fr' ? 'Pièces jointes obligatoires' : 'Required attachments'}
              </div>
              <div className="bg-emerald-50 rounded-md p-3 text-sm space-y-1 border border-emerald-200">
                <div className="flex items-center gap-2 text-emerald-800">
                  <CheckCircle2 className="w-4 h-4" />
                  {language === 'fr' ? 'Export PDF complet (toutes les pages + Décision Direction)' : 'Full PDF export'}
                </div>
                <div className="flex items-center gap-2 text-emerald-800">
                  <CheckCircle2 className="w-4 h-4" />
                  {language === 'fr' ? 'Export Excel complet (tous les onglets)' : 'Full Excel export'}
                </div>
              </div>
            </div>

            {hgModalPreview?.has_contract_attachments && (
              <label className="flex items-start gap-3 cursor-pointer p-3 rounded-md border-2 border-amber-300 bg-amber-50 hover:bg-amber-100 transition-colors">
                <Checkbox
                  checked={hgModalIncludeContracts}
                  onCheckedChange={setHgModalIncludeContracts}
                  className="mt-0.5"
                  data-testid="hg-include-contracts-checkbox-dashboard"
                />
                <div className="flex-1">
                  <div className="font-semibold text-amber-900 text-sm">
                    {language === 'fr'
                      ? `Inclure également le(s) contrat(s) signé(s) (${hgModalPreview.contract_attachments.length})`
                      : `Also include signed contract(s) (${hgModalPreview.contract_attachments.length})`}
                  </div>
                  <div className="mt-1 space-y-0.5">
                    {hgModalPreview.contract_attachments.map((a, i) => (
                      <div key={i} className="text-xs text-amber-700 flex items-center gap-1">
                        <FileText className="w-3 h-3" />
                        {a.original_name}
                        <span className="text-amber-500 ml-1">({(a.size_bytes / 1024).toFixed(1)} KB)</span>
                      </div>
                    ))}
                  </div>
                </div>
              </label>
            )}

            {/* Optional NB / Note */}
            <div>
              <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">
                📝 {language === 'fr' ? 'NB / Note pour HG' : 'NB / Note for HG'}
                <span className="ml-2 text-[10px] font-normal text-slate-400 normal-case tracking-normal">
                  ({language === 'fr' ? 'optionnel — apparaîtra en haut de l\'email' : 'optional — will appear at the top of the email'})
                </span>
              </label>
              <Textarea
                value={hgModalNote}
                onChange={e => setHgModalNote(e.target.value)}
                placeholder={language === 'fr'
                  ? 'Ex: Contre-proposition acceptée à 18% au lieu de 15%. Dossier prioritaire pour ouverture en juin.'
                  : 'E.g. Counter-proposal accepted at 18% instead of 15%. Priority dossier for June opening.'}
                rows={3}
                className="text-sm border-amber-200 focus:border-amber-400 focus:ring-amber-300"
                maxLength={1000}
                data-testid="hg-note-textarea-dashboard"
              />
              {hgModalNote && (
                <div className="text-[10px] text-slate-400 text-right mt-1">{hgModalNote.length} / 1000</div>
              )}
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setHgModalSimId(null)}
              disabled={sendingToHg === hgModalSimId}
              data-testid="hg-cancel-btn-dashboard"
            >
              {language === 'fr' ? 'Annuler' : 'Cancel'}
            </Button>
            <Button
              onClick={() => handleSendToHg(hgModalSimId)}
              disabled={sendingToHg === hgModalSimId}
              className="bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white gap-2"
              data-testid="hg-confirm-send-btn-dashboard"
            >
              {sendingToHg === hgModalSimId ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
              {language === 'fr' ? 'Envoyer à HG' : 'Send to HG'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {renderBsPrompt()}
    </AppLayout>
  );
}
